import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LoaderSpinner from '../common/Loader';
import ViewImage from '../common/ViewImage';
import ChargeCarrierConnect from './ChargeCarrierConnect';
import { toast } from "react-toastify";
import { CARRIER_CONNECT_STATUS, featureModule, FEATURE_CARRIERCONNECTMANAGEMENT, permssionBulkUpload, CMSEnum, InchEnum, WEIGHT_UNIT_GM, WEIGHT_UNIT_KG, WEIGHT_UNIT_LB, WEIGHT_UNIT_OZ } from '../../constant';
import { getDimensionUnitList, getWeightUnitList, getAllServices, getAllPackagesByServiceId, getAllSellersList } from '../../store/actions';
import { getAllConsignmentCarrierType, getAllConsignmentStatus, getConsignmentStatusListById } from '../../store/actions/inventory.action';
import { 
    getCarrierConnectById, 
    createCarrierConnect,
    updateCarrierConnectMedia,
    updateCarrierConnect
} from '../../store/actions/carrierConnect.action';
import './CarrierConnect.css';
import Select from "react-select";
import { checkPermission, getSelectList, computeAutoSelectedSeller } from '../../config/utils';
import { BulkPackageUploadDialog } from '../common/BulkPackageUploadDialog';
import { fetchCompanyFilters, getFilterSellers } from '../../store/actions/rateCard';
import ChargesHistory from './ChargesHistory';
import AlertDialogModal from '../common/AlertDialog';

// Constants
const ADDITIONAL_NOTES_PREFIX = 'Added by 3plVision: ';

/* istanbul ignore next */
// NOSONAR: Cognitive complexity temporarily suppressed for incremental refactor.
// extract large render and several handlers into smaller components/helpers to reduce complexity below threshold.
// Helper factory to produce react-select option style function
const makeOptionStyle = (selectOptionBackground) => (provided, state) => {
    const disabled = !!state.isDisabled;
    const selected = !!state.isSelected;
    let colorValue = '#495057';
    if (disabled) {
        colorValue = '#6c757d';
    } else if (selected) {
        colorValue = 'white';
    }

    return ({
        ...provided,
        padding: '8px 12px',
        fontSize: '12px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: disabled ? '#f8f9fa' : selectOptionBackground(state),
        color: colorValue,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        '&:hover': disabled ? {} : {
            backgroundColor: selected ? '#007bff' : '#f8f9fa'
        }
    });
};
/* istanbul ignore next */
const CarrierConnectConsole = (props) => { // NOSONAR
    const { id } = useParams();
    const [loader, setLoader] = useState(false);
    const [showChargesDetails, setShowChargesDetails] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showChargeAlert, setShowChargeAlert] = useState(false);
    const [formData, setFormData] = useState({
        carrierConnectId: '',
        carrierStatus: '',
        carrierId: '',
        companyId: '',
        sellerId: '',
        trackingNumber: '',
        subTrackingNumber: '',
        length: '',
        width: '',
        height: '',
        weight: '',
        dimensionUnitId: '',
        weightUnitId: '',
        notes: '',
        packageCount: '', // Package count at carrier connect level
        createdDate: '' // Created date from API
    });
    
    const [errors, setErrors] = useState({});
    const [isEditable, setIsEditable] = useState(location?.state?.isEdit !== false);
    const [originalStatus, setOriginalStatus] = useState(null); // Track the original/saved status
    const userDetails = useSelector((state) => state.auth.user);
    const { dimensionUnitList, weightUnitList } = useSelector((state) => state.shipment);
    const services = useSelector((state) => state.shipment?.services);
    const packages = useSelector((state) => state.shipment?.packages);
    const { consignmentAllStatus } = useSelector((state) => state.InventoryManagement);
    const carrierConnectDetails = useSelector((state) => state.carrierConnect?.carrierConnectDetails);
    const { filterCompanies, filterAllSellers } = useSelector((state) => state.RateCardReducer);

    // Helper function to check if user has warehouse role (cannot create new carrier connects)
    const isWarehouseRole = () => {
        const roleName = (userDetails?.Value?.roleName || '').toString().trim().toLowerCase();
        return roleName === 'warehouse manager' || roleName === 'warehouse worker';
    };

    // Helper function to check if user can see additional notes field (warehouse roles + super admin)
    const canSeeAdditionalNotes = () => {
        const roleName = (userDetails?.Value?.roleName || '').toString().trim().toLowerCase();
        const isSuperAdmin = userDetails?.Value?.isSuperAdmin;
        return roleName === 'warehouse manager' || roleName === 'warehouse worker' || isSuperAdmin;
    };

    // Helper function to check if main notes should be read-only (for warehouse roles and super admin)
    const shouldMainNotesBeReadOnly = () => {
        // Main notes should be read-only only when additional notes field is visible
        // i.e., for roles with additional notes access, in edit mode, and when status is NOT New and NOT Closed
        return canSeeAdditionalNotes() && Number(id) > 0 && isEditable && !isNewStatus() && !isClosedStatus();
    };

    // Additional notes state for warehouse roles and super admin
    const [additionalNotes, setAdditionalNotes] = useState('');

    // Redirect warehouse roles away from create page
    useEffect(() => {
        if (Number(id) === 0 && isWarehouseRole()) {
            toast.error('You do not have permission to create new carrier connects.');
            navigate('/carrier-connect');
            return;
        }
    }, [id, userDetails, navigate]);
    
    // Additional states for images and attachments
    const [image, setImage] = useState([]);
    const [attachmentFile, setAttachmentFile] = useState([]);
    
    // States to track which existing media should be deleted
    const [masterImageIdsToDelete, setMasterImageIdsToDelete] = useState([]);
    const [masterAttachmentIdsToDelete, setMasterAttachmentIdsToDelete] = useState([]);
    
    // Separate state for package images and attachments
    const [packageImages, setPackageImages] = useState([]);
    const [packageAttachments, setPackageAttachments] = useState([]);
    
    // Image viewing modal state
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageRecord, setSelectedImageRecord] = useState({});
    
    // Package view modal state
    const [showPackageViewModal, setShowPackageViewModal] = useState(false);
    const [selectedPackageForView, setSelectedPackageForView] = useState({});
    
    // Charge modal state
    const [showCharge, setShowCharge] = useState(false);
    const [csvModalShow, setCsvModalShow] = useState(false);
    
    const [servicePackages, setServicePackages] = useState([]);
    const [carriersData, setCarriersData] = useState([]);
    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    
    
    // Package management states
    const [packagesList, setPackagesList] = useState([]);
    const [originalPackagesList, setOriginalPackagesList] = useState([]); // Track original packages for updates
    const [showPackageForm, setShowPackageForm] = useState(false);
    const [currentPackage, setCurrentPackage] = useState({
        length: '',
        width: '',
        height: '',
        weight: '',
        dimensionUnitId: '',
        weightUnitId: '',
        trackingNumber: '',
        packageType: '',
        carrierCompanyId: '',
        serviceId: '',
        packageId: '',
        images: [],
        attachments: [],
        notes: '',
        packageCount: '',
        createdDate: '' // Created date from API
    });
    const [editingPackageIndex, setEditingPackageIndex] = useState(-1);
    const selectOptionBackground = (state) => {
    // Use explicit if-statements instead of nested ternary to reduce cognitive complexity
    if (state?.isSelected) {
        return '#007bff';
    }
    if (state?.isFocused) {
        return '#f8f9fa';
    }
    return 'white';
};
    // Tracking number length constraints
    const TRACKING_MIN_LENGTH = 1; // minimum meaningful length
    const TRACKING_MAX_LENGTH = 50; // maximum allowed characters for tracking numbers
    const SUB_TRACKING_MAX_LENGTH = 50; // maximum allowed characters for sub-tracking numbers
    // Fetch carriers on component mount
    useEffect(() => {
        // Call fetchCarriers for the main carrier dropdown
        fetchCarriers();

        // Call getAllServices the same way outbound does for US domestic (country_Id = 218, no military state)
        // This matches the outbound component's logic: dispatch(getAllServices(false, false))
        dispatch(getAllServices(false, false));

        // Fetch companies and sellers for super admin users using Redux approach
        if (userDetails?.Value?.isSuperAdmin) {
            let data = {
                pageIndex: 1,
                pageSize: 100,
                sortColumn: "name",
                sortOrder: "ascending",
            };
            let sellerData = {
                "pageIndex": 1,
                "pageSize": 100,
                "sortColumn": "name",
                "sortOrder": "ASCENDING",
                "companyIdList": [0],
                screenType: 2,
            };
            dispatch(fetchCompanyFilters(data));
            dispatch(getFilterSellers(sellerData));
        }
    }, []);

    // Update service packages when package carrier changes (for individual packages)
    useEffect(() => {
        if (services?.length && currentPackage.carrierCompanyId) {
            let carrierServices = services.filter((item) => item.carrierCompanyId === Number(currentPackage.carrierCompanyId)); // NOSONAR
            setServicePackages(carrierServices[0] || []);
        }
    }, [currentPackage.carrierCompanyId, services]);

    // Fetch sellers when company changes
    useEffect(() => {
        if (formData.companyId && userDetails?.Value?.isSuperAdmin) {
            fetchSellers(formData.companyId);
        } else if (!formData.companyId) {
            setSellerList([]);
        }
    }, [formData.companyId]);

    // Populate company and seller lists from Redux state
    useEffect(() => {
        let companyList = filterCompanies?.Entity?.listCompanyMasterOrdersEntity || [];
        let sellerList = filterAllSellers?.EntityList || [];
        if (companyList.length) {
            setCompanyList(getSelectList(filterCompanies?.Entity?.listCompanyMasterOrdersEntity, 'Id', 'name'));
        }
        if (sellerList.length) {
            setSellerList(getSelectList(sellerList, 'id', 'sellerName'));
        }
    }, [filterCompanies, filterAllSellers]);

    // Fetch packages when package service type changes
    useEffect(() => {
        if (currentPackage.serviceId) {
            dispatch(getAllPackagesByServiceId(currentPackage.serviceId));
        }
    }, [currentPackage.serviceId]);

    // Cache for packages by service ID to avoid repeated API calls
    const [servicePackagesCache, setServicePackagesCache] = useState({});
    const [loadingPackagesForServices, setLoadingPackagesForServices] = useState(new Set());

    // Load packages for common services when services are loaded
    useEffect(() => {
        if (services?.length > 0 && (!packages || packages.length === 0)) {
            // Try to load packages for the first available service to populate the packages list
            const firstService = services[0];
            if (firstService?.id) {
                dispatch(getAllPackagesByServiceId(firstService.id));
            }
        }
    }, [services]);

    // Helper function to handle package loading for a specific service ID
    const loadPackagesForService = (serviceId) => {
        if (!serviceId || servicePackagesCache[serviceId] || loadingPackagesForServices.has(serviceId)) {
            return Promise.resolve();
        }

        setLoadingPackagesForServices(prev => new Set([...prev, serviceId]));
        
        return dispatch(getAllPackagesByServiceId(serviceId))
            .then((packagesResult) => {
                if (packagesResult && packagesResult.length > 0) {
                    setServicePackagesCache(prev => ({
                        ...prev,
                        [serviceId]: packagesResult
                    }));
                }
            })
            .catch((error) => {
                // Package loading error handled gracefully
            })
            .finally(() => {
                setLoadingPackagesForServices(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(serviceId);
                    return newSet;
                });
            });
    };

    // Load packages for all unique services in packagesList to enable proper package name lookups
    useEffect(() => {
        if (packagesList?.length > 0 && services?.length > 0) {
            const uniqueServiceIds = [...new Set(packagesList.map(pkg => pkg.serviceId))];
            
            // Use Promise.all with map instead of forEach to properly handle async operations
            const packageLoadPromises = uniqueServiceIds.map(loadPackagesForService);
            
            // Execute all package loading operations in parallel
            Promise.all(packageLoadPromises);
        }
    }, [packagesList, services]);

    const fetchCarriers = async () => {
        try {
            const res = await getAllConsignmentCarrierType();
            if (res.data.Status === 200) {
                setCarriersData(res.data.EntityList);
            }
        } catch (error) {
            console.error('Error fetching carriers:', error);
            setCarriersData([]); // Ensure carriers data is reset on error
        }
    };

    // Map a CSV row to a package object and optionally return messages for missing mappings
    // mapCsvRow accepts an optional localServicePackages cache so callers can
    // provide freshly fetched package lists without waiting for React state updates.
    // Helper function to extract values from CSV row with flexible column name matching
    const getValueFromRow = (row, names) => {
        for (const name of names) {
            if (row.hasOwnProperty(name)) return row[name];
        }
        const keys = Object.keys(row);
        for (const key of keys) {
            for (const name of names) {
                if (key.trim().toLowerCase() === name.trim().toLowerCase()) return row[key];
            }
        }
        return '';
    };

    // Helper function to resolve carrier ID from name or ID
    const resolveCarrierId = (rawCarrier, services) => {
        if (!rawCarrier) return { id: '', message: `Carrier '${rawCarrier || ''}' not found` };

        const asId = parseInt(rawCarrier); // NOSONAR
        if (!isNaN(asId) && services?.length) { // NOSONAR
            const byId = services.find(c => parseInt(c.carrierCompanyId) === asId); // NOSONAR
            if (byId) return { id: asId, message: null };
        }

        if (services?.length) {
            const nl = normalizeLabel(rawCarrier);
            const found = services.find(c => normalizeLabel((c.carrierCompanyName || c.carrierName || c.name) || '') === nl);
            if (found) return { id: found.carrierCompanyId, message: null };
        }

        return { id: '', message: `Carrier '${rawCarrier || ''}' not found` };
    };

    // Helper function to find service by normalized name in a carrier's services
    const findServiceByName = (services, rawService) => {
        if (!services?.length || !rawService) return null;
        
        const nl = normalizeLabel(rawService);
        return services.find(svc => normalizeLabel((svc.serviceName || svc.name) || '') === nl);
    };

    // Helper function to search for service in a specific carrier
    const searchServiceInCarrier = (carrierEntry, rawService) => {
        if (!carrierEntry?.services?.length) {
            return null;
        }
        return findServiceByName(carrierEntry.services, rawService);
    };

    // Helper function to search for service across all carriers
    const searchServiceInAllCarriers = (services, rawService) => {
        for (const carrierEntry of services) {
            const foundService = searchServiceInCarrier(carrierEntry, rawService);
            if (foundService) {
                return foundService;
            }
        }
        return null;
    };

    // Helper function to resolve service ID from name or ID
    const resolveServiceId = (rawService, resolvedCarrierId, services) => {
        if (!rawService) return { id: '', message: `Service '${rawService || ''}' not found` };

        const asId = parseInt(rawService); // NOSONAR
        if (!isNaN(asId)) { // NOSONAR
            return { id: asId, message: null };
        }

        if (!services?.length) {
            return { id: '', message: `Service '${rawService || ''}' not found` };
        }

        let foundService = null;

        if (resolvedCarrierId) {
            const carrierEntry = services.find(s => parseInt(s.carrierCompanyId) === parseInt(resolvedCarrierId)); // NOSONAR
            foundService = searchServiceInCarrier(carrierEntry, rawService);
        } else {
            foundService = searchServiceInAllCarriers(services, rawService);
        }

        if (foundService) {
            return { id: foundService.id, message: null };
        }

        return { id: '', message: `Service '${rawService || ''}' not found` };
    };

    // Helper function to get package label and ID from package object
    const getPackageInfo = (p) => {
        const getPackageLabel = (pkg) => {
            return (pkg && (pkg.packageTypeName || pkg.packageType || pkg.packageName || pkg.name || pkg.pkgType || pkg.displayName || pkg.title || pkg.label || pkg.valueName || pkg.value || '')) || '';
        };

        const getPackageId = (pkg) => {
            if (!pkg) return '';
            return pkg.id || pkg.packageId || pkg.package_Id || pkg.packageID || pkg.PackageId || '';
        };

        return { label: getPackageLabel(p), id: getPackageId(p) };
    };

    // Helper function to search packages in a list
    const searchPackagesInList = (rawPackage, packageList) => {
        if (!packageList?.length || !rawPackage) return null;

        const nl = normalizeLabel(rawPackage);
        const nlCompact = nl.replace(/\s+/g, ''); // NOSONAR

        // Exact match
        const exactMatch = packageList.find(p => {
            const { label } = getPackageInfo(p);
            const candidate = normalizeLabel(label);
            const candidateCompact = candidate.replace(/\s+/g, ''); // NOSONAR
            return candidate === nl || candidateCompact === nlCompact;
        });
        if (exactMatch) return exactMatch;

        // Substring fallback
        const substringMatch = packageList.find(p => {
            const { label } = getPackageInfo(p);
            const candidate = normalizeLabel(label);
            const candidateCompact = candidate.replace(/\s+/g, ''); // NOSONAR
            return candidate.includes(nl) || nl.includes(candidate) || candidateCompact.includes(nlCompact) || nlCompact.includes(candidateCompact);
        });
        return substringMatch;
    };

    // Helper function to resolve package ID from name or ID
    const resolvePackageId = (rawPackage, resolvedServiceId, packages, localServicePackages, servicePackagesCache) => {
        if (!rawPackage) return { id: '', message: `Package '${rawPackage || ''}' not found.`, candidates: [] };

        const asId = parseInt(rawPackage); // NOSONAR
        if (!isNaN(asId)) { // NOSONAR
            return { id: asId, message: null, candidates: [] };
        }

        let candidatePackageNames = [];

        // Search in global packages first
        if (packages?.length) {
            const globalCandidates = packages.map(p => normalizeLabel(getPackageInfo(p).label));
            candidatePackageNames = candidatePackageNames.concat(globalCandidates);

            const foundGlobal = searchPackagesInList(rawPackage, packages);
            if (foundGlobal) {
                return { id: getPackageInfo(foundGlobal).id, message: null, candidates: [] };
            }
        }

        // Search in service-specific packages
        const effectiveServicePackages = localServicePackages || servicePackagesCache;
        if (resolvedServiceId && effectiveServicePackages?.[resolvedServiceId]) {
            const svcPkgs = effectiveServicePackages[resolvedServiceId];
            const svcCandidates = svcPkgs.map(p => normalizeLabel(getPackageInfo(p).label));
            candidatePackageNames = candidatePackageNames.concat(svcCandidates);

            const foundSvc = searchPackagesInList(rawPackage, svcPkgs);
            if (foundSvc) {
                return { id: getPackageInfo(foundSvc).id, message: null, candidates: [] };
            }
        }

        const uniqueCandidates = Array.from(new Set(candidatePackageNames)).slice(0, 10).filter(Boolean);
        const message = uniqueCandidates.length 
            ? `Package '${rawPackage || ''}' not found. Candidates: ${uniqueCandidates.join(', ')}`
            : `Package '${rawPackage || ''}' not found.`;

        return { id: '', message, candidates: uniqueCandidates };
    };

    // Helper function to get unit label with fallbacks
    const getUnitLabel = (unit) => {
        return (unit.unitName || unit.name || unit.displayName || unit.label || unit.code || unit.valueName || unit.value || '') || '';
    };

    // Helper function to get unit ID with fallbacks
    const getUnitId = (unit) => {
        return unit.id || unit.value || '';
    };

    // Helper function to find unit by ID
    const findUnitById = (list, rawVal) => {
        const asId = parseInt(rawVal); // NOSONAR
        if (isNaN(asId)) return null; // NOSONAR
        
        return list.find(u => Number(u.id) === asId || Number(u.value) === asId);
    };

    // Helper function to find unit by exact label match
    const findUnitByExactMatch = (list, rawVal) => {
        const nl = normalizeLabel(rawVal);
        return list.find(u => normalizeLabel(getUnitLabel(u)) === nl);
    };

    // Helper function to find unit by substring match
    const findUnitBySubstringMatch = (list, rawVal) => {
        const nlRaw = normalizeLabel(rawVal);
        return list.find(u => {
            const nlLabel = normalizeLabel(getUnitLabel(u));
            return nlLabel.includes(nlRaw) || nlRaw.includes(nlLabel);
        });
    };

    // Helper function to resolve unit ID from name or ID
    const resolveUnitId = (rawVal, list) => {
        if (!rawVal) return '';
        if (!list?.length) return '';
        
        // Try to find by ID first
        const unitById = findUnitById(list, rawVal);
        if (unitById) return parseInt(rawVal); // NOSONAR
        
        // Try exact label match
        const unitByExactMatch = findUnitByExactMatch(list, rawVal);
        if (unitByExactMatch) return getUnitId(unitByExactMatch);
        
        // Try substring fallback
        const unitBySubstring = findUnitBySubstringMatch(list, rawVal);
        if (unitBySubstring) return getUnitId(unitBySubstring);
        
        return '';
    };

    // Helper function to validate unit consistency
    const validateUnitConsistency = (resolvedWeightUnitId, resolvedDimensionUnitId, weightUnitList, dimensionUnitList) => {
        const messages = [];
        
        const getUnitName = (list, id) => {
            if (!list?.length || !id && id !== 0) return id;
            const found = list.find(u => Number(u.id) === Number(id) || Number(u.value) === Number(id));
            return found ? (found.unitName || found.name || found.displayName || found.label || String(id)) : String(id);
        };

        try {
            if (resolvedWeightUnitId && resolvedDimensionUnitId) {
                const w = Number(resolvedWeightUnitId);
                const d = Number(resolvedDimensionUnitId);
                const isMetricWeight = (w === WEIGHT_UNIT_KG || w === WEIGHT_UNIT_GM);
                const isImperialWeight = (w === WEIGHT_UNIT_LB || w === WEIGHT_UNIT_OZ);
                const isDimCms = (d === CMSEnum);
                const isDimInch = (d === InchEnum);

                if (isMetricWeight && !isDimCms) {
                    messages.push(`Inconsistent units: weight unit '${getUnitName(weightUnitList, w)}' usually maps to dimension unit '${getUnitName(dimensionUnitList, CMSEnum)}'`);
                } else if (isImperialWeight && !isDimInch) {
                    messages.push(`Inconsistent units: weight unit '${getUnitName(weightUnitList, w)}' usually maps to dimension unit '${getUnitName(dimensionUnitList, InchEnum)}'`);
                } else if (isDimInch && !isImperialWeight) {
                    messages.push(`Inconsistent units: dimension unit '${getUnitName(dimensionUnitList, d)}' usually maps to weight unit '${getUnitName(weightUnitList, WEIGHT_UNIT_LB)}'`);
                } else if (isDimCms && !isMetricWeight) {
                    messages.push(`Inconsistent units: dimension unit '${getUnitName(dimensionUnitList, d)}' usually maps to weight unit '${getUnitName(weightUnitList, WEIGHT_UNIT_KG)}'`);
                }
            }
        } catch (e) {
            console.debug('[CSV Validate] unit consistency check failed', e);
        }

        return messages;
    };

    const mapCsvRow = (row = {}, localServicePackages = null) => {
        const getVal = (names) => getValueFromRow(row, names);

        const rawCarrier = (getVal(['Carrier*','Carrier']) || '').toString().trim();
        const rawService = (getVal(['Service*','Service']) || '').toString().trim();
        const rawPackage = (getVal(['Package*','Package']) || '').toString().trim();
        const rawWeightUnit = (getVal(['WeightUnit*','WeightUnit']) || '').toString().trim();
        const rawDimensionUnit = (getVal(['DimensionUnit*','DimensionUnit']) || '').toString().trim();

        const messages = [];

        // Resolve carrier
        const carrierResult = resolveCarrierId(rawCarrier, services);
        if (carrierResult.message) messages.push(carrierResult.message);

        // Resolve service
        const serviceResult = resolveServiceId(rawService, carrierResult.id, services);
        if (serviceResult.message) messages.push(serviceResult.message);

        // Resolve package
        const packageResult = resolvePackageId(rawPackage, serviceResult.id, packages, localServicePackages, servicePackagesCache);
        if (packageResult.message) {
            messages.push(packageResult.message);
            
            // Debug logging for package resolution
            if (packageResult.candidates.length === 0) {
                const globalLabels = (packages || []).map(p => (p.packageName || p.name || p.pkgType || p.displayName || '')).filter(Boolean).slice(0,10);
                const svcLabels = (serviceResult.id && (localServicePackages || servicePackagesCache) && (localServicePackages || servicePackagesCache)[serviceResult.id]) ?
                    (localServicePackages || servicePackagesCache)[serviceResult.id].map(p => (p.packageName || p.name || p.pkgType || p.displayName || '')).filter(Boolean).slice(0,10) : [];

                console.debug('[CSV Validate] package match failed', {
                    resolvedServiceId: serviceResult.id || 'none',
                    packagesCount: (packages || []).length,
                    uniqueCandidates: packageResult.candidates,
                    samplePackages: globalLabels,
                    servicePackagesCount: svcLabels.length,
                    sampleServicePackages: svcLabels
                });
            }
        }

        // Resolve units
        const resolvedWeightUnitId = resolveUnitId(rawWeightUnit, weightUnitList || []);
        const resolvedDimensionUnitId = resolveUnitId(rawDimensionUnit, dimensionUnitList || []);

        if (!resolvedWeightUnitId && rawWeightUnit) {
            messages.push(`WeightUnit '${rawWeightUnit}' not found`);
        }
        if (!resolvedDimensionUnitId && rawDimensionUnit) {
            messages.push(`DimensionUnit '${rawDimensionUnit}' not found`);
        }

        // Validate unit consistency
        const consistencyMessages = validateUnitConsistency(resolvedWeightUnitId, resolvedDimensionUnitId, weightUnitList, dimensionUnitList);
        messages.push(...consistencyMessages);

        const mappedPkg = {
            id: 0,
            carrierConnectPackageNumber: `CCP${Date.now()}`,
            pkgType: getVal(['PkgType*','PkgType','pkgType']) || '',
            packageType: getVal(['PkgType*','PkgType','pkgType']) || '',
            trackingNumber: normalizeNumber(getVal(['TrackingNumber','trackingNumber'])) || '',
            length: parseFloat(getVal(['Length*','Length']) || 0) || '', // NOSONAR
            width: parseFloat(getVal(['Width*','Width']) || 0) || '', // NOSONAR
            height: parseFloat(getVal(['Height*','Height']) || 0) || '', // NOSONAR
            weight: parseFloat(getVal(['Weight*','Weight']) || 0) || '', // NOSONAR
            dimensionUnitId: resolvedDimensionUnitId || getVal(['DimensionUnit*','DimensionUnit']) || '',
            weightUnitId: resolvedWeightUnitId || getVal(['WeightUnit*','WeightUnit']) || '',
            packageCount: parseInt(getVal(['PackageCount','Package']) || 1) || 1, // NOSONAR
            carrierCompanyId: carrierResult.id || getVal(['Carrier*','Carrier']) || '',
            serviceId: serviceResult.id || getVal(['Service*','Service']) || '',
            packageId: packageResult.id || getVal(['Package*','Package']) || '',
            notes: getVal(['Notes','notes']) || ''
        };

        return { mappedPkg, messages };
    };

    const normalizeNumber = (val) => {
        if (typeof val === "string" && /e\+/i.test(val)) {
            // Convert scientific notation string to a full number string
            const [coeff, exp] = val.toLowerCase().split('e+');
            let [intPart, decPart = ""] = coeff.split(".");
            const zerosToAdd = parseInt(exp, 10) - decPart.length; // NOSONAR
            return intPart + decPart + "0".repeat(Math.max(0, zerosToAdd));
        }
        return val;
    };



    const fetchSellers = async (companyId) => {
        if (!companyId) {
            setSellerList([]);
            return;
        }

        try {
            let data = {
                companyIds: [companyId],
            };

            const res = await getAllSellersList(data);

            if (res?.data?.Status === 200 && res?.data?.Entity?.listSellerMasterOrdersEntity?.length) {
                const sellerList = res.data.Entity.listSellerMasterOrdersEntity.map(
                    (item) => {
                        return {
                            ...item,
                            label: item.sellerAndCompany,
                            value: item.id,
                        };
                    }
                );
                setSellerList(sellerList);
                // Auto-select the lone seller when the company has exactly one
                const lone = computeAutoSelectedSeller(sellerList);
                if (lone) {
                    setFormData((prev) => (prev?.sellerId ? prev : { ...prev, sellerId: lone.value }));
                    setErrors((prev) => ({ ...prev, sellerId: '' }));
                }
            } else {
                setSellerList([]);
            }
        } catch (error) {
            setSellerList([]);
        }
    };

    // Package management functions
    const handleAddPackage = () => {
        setShowPackageForm(true);
        setEditingPackageIndex(-1);
        // Clear package-specific images and attachments
        setPackageImages([]);
        setPackageAttachments([]);
        setCurrentPackage({
            length: '',
            width: '',
            height: '',
            weight: '',
            dimensionUnitId: '',
            weightUnitId: '',
            trackingNumber: '', // Independent tracking number for each package
            packageType: '',
            carrierCompanyId: '',
            serviceId: '',
            packageId: '',
            images: [],
            attachments: [],
            notes: '',
            createdDate: ''
        });
    };

    const handlePackageFormValue = (e) => {
        const { name, value } = e.target;
        setCurrentPackage({ ...currentPackage, [name]: value });
        
        // Clear field errors for package fields when user makes changes
        const packageFieldKey = `package_${editingPackageIndex}_${name}`;
        setErrors(prev => ({ ...prev, [packageFieldKey]: '' }));
    };

    const handleSavePackage = () => {
        // Include package images and attachments in the saved package
        const packageToSave = {
            ...currentPackage,
            id: editingPackageIndex >= 0 ? currentPackage.id : 0, // Preserve original ID when editing
            images: packageImages,
            attachments: packageAttachments
        };

        if (editingPackageIndex >= 0) {
            // Update existing package
            const updatedPackages = [...packagesList];
            updatedPackages[editingPackageIndex] = packageToSave;
            setPackagesList(updatedPackages);
        } else {
            // Add new package
            setPackagesList([...packagesList, packageToSave]);
        }
        
        setShowPackageForm(false);
        // Clear package form and images/attachments
        setPackageImages([]);
        setPackageAttachments([]);
        setCurrentPackage({
            length: '',
            width: '',
            height: '',
            weight: '',
            dimensionUnitId: '',
            weightUnitId: '',
            trackingNumber: '', // Independent tracking number
            packageType: '',
            carrierCompanyId: '',
            serviceId: '',
            packageId: '',
            images: [],
            attachments: [],
            notes: ''
        });
    };

    const handleEditPackage = (index) => {
        const packageData = packagesList[index];
        setCurrentPackage(packageData);
        // Load package's images and attachments into separate state
        setPackageImages(packageData.images || []);
        setPackageAttachments(packageData.attachments || []);
        setEditingPackageIndex(index);
        setShowPackageForm(true);
    };

    const handleViewPackage = (index) => {
        const packageData = packagesList[index];
        setSelectedPackageForView({
            ...packageData,
            images: packageData.images || [],
            attachments: packageData.attachments || []
        });
        setShowPackageViewModal(true);
    };

    const handleClosePackageViewModal = () => {
        setShowPackageViewModal(false);
        setSelectedPackageForView({});
    };

    const handleDeletePackage = (index) => {
        const updatedPackages = packagesList.filter((_, i) => i !== index);
        setPackagesList(updatedPackages);
    };

    const handleCancelPackageForm = () => {
        setShowPackageForm(false);
        // Clear package-specific images and attachments
        setPackageImages([]);
        setPackageAttachments([]);
        setCurrentPackage({
            length: '',
            width: '',
            height: '',
            weight: '',
            dimensionUnitId: '',
            weightUnitId: '',
            trackingNumber: '', // Independent tracking number
            packageType: '',
            carrierCompanyId: '',
            serviceId: '',
            packageId: '',
            images: [],
            attachments: [],
            notes: '',
            createdDate: ''
        });
        setEditingPackageIndex(-1);
    };

    // Functions for viewing images and downloading attachments
    const handleViewImage = (imageRecord) => {
        setSelectedImageRecord(imageRecord);
        setShowImageModal(true);
    };

    const handleDownloadAttachment = (attachmentUrl, fileName) => {
        try {
            const link = document.createElement('a');
            link.href = attachmentUrl;
            link.download = fileName || 'attachment';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link); // NOSONAR
        } catch (error) {
            toast.error('Failed to download attachment');
        }
    };

    const renderImageThumbnail = (imageItem, index, isPackageLevel = false) => {
        const imageUrl = imageItem.imageUrl || imageItem.filePath || imageItem.url;
        const fileName = imageItem.fileName || imageItem.name || `Image ${index + 1}`;
        
        return (
            <div key={index} className="d-inline-block m-2 position-relative mw-120">
                <button 
                    className="image-thumbnail cursor-pointer border rounded p-2"
                    onClick={() => handleViewImage({ imageUrl, fileName })}
                    aria-label={`View image: ${fileName}`}
                    style={{ 
                        width: '120px', 
                        height: '100px', 
                        overflow: 'hidden',
                        background: 'none',
                        border: '1px solid #dee2e6',
                        padding: '0.5rem',
                        borderRadius: '0.25rem'
                    }}
                >
                    <img 
                        src={imageUrl} 
                        alt={fileName}
                        className="w-100 h-100"
                        style={{ objectFit: 'cover' }}
                        onError={(e) => {
                            e.target.src = '/path/to/default-image.png'; // Add default image path
                            e.target.alt = 'Image not available';
                        }}
                    />
                </button>
                <small className="text-muted d-block text-center mt-1" style={{ fontSize: '10px', wordWrap: 'break-word' }}>
                    {fileName}
                </small>
            </div>
        );
    };

    const renderAttachmentItem = (attachmentItem, index) => {
        const attachmentUrl = attachmentItem.fileUrl || attachmentItem.filePath || attachmentItem.url;
        const fileName = attachmentItem.fileName || attachmentItem.name || `Attachment ${index + 1}`;
        const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
        
        return (
            <div key={index} className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                <div className="d-flex align-items-center">
                    <i className={`fas fa-file${getFileIcon(fileExtension)} me-2`}></i>
                    <span>{fileName}</span>
                </div>
                <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleDownloadAttachment(attachmentUrl, fileName)}
                    title="Download attachment"
                >
                    <i className="fas fa-download"></i>
                </button>
            </div>
        );
    };

    const getFileIcon = (extension) => {
        switch (extension) {
            case 'pdf': return '-pdf';
            case 'doc':
            case 'docx': return '-word';
            case 'xls':
            case 'xlsx': return '-excel';
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif': return '-image';
            case 'zip':
            case 'rar': return '-archive';
            default: return '';
        }
    };
    
    const [statuses, setStatuses] = useState([]);

    // Load dimension and weight units
    useEffect(() => {
        if (!dimensionUnitList.length) {
            dispatch(getDimensionUnitList());
        }
        if (!weightUnitList.length) {
            dispatch(getWeightUnitList());
        }
        // Removed duplicate fetchCarriers() and getAllServices() calls
        // These are already called in the main component mount useEffect
    }, [dimensionUnitList, weightUnitList, dispatch]);

    // Load consignment status list
    useEffect(() => {
        if (!consignmentAllStatus?.length) {
            dispatch(getAllConsignmentStatus());
        } else {
            setStatuses(consignmentAllStatus);
            // Set default status for new connections
            if (Number(id) === 0 && !formData.carrierStatus && consignmentAllStatus.length > 0) {
                const defaultStatus = consignmentAllStatus.find(status => status.name === 'New') || consignmentAllStatus[0];
                setFormData(prev => ({
                    ...prev,
                    carrierStatus: defaultStatus.id
                }));
            }
        }
    }, [consignmentAllStatus, dispatch, id, formData.carrierStatus]);

    // Fetch detailed status information for tooltip
    const fetchStatuses = () => {
        if (Number(id) > 0) {
            getConsignmentStatusListById(id).then(res => {
                console.log('Fetched status details:', res.data);
            });
        }
    };

    useEffect(() => {
        if (Number(id) === 0) {
            // New connection - generate ID
            setFormData(prev => ({
                ...prev,
                carrierConnectId: `CC${Date.now()}`,
                carrierStatus: '' // Will be set when statuses are loaded
            }));
            // For new records, there's no original status restriction
            setOriginalStatus(null);
        } else {
            // Edit existing connection - fetch data
            fetchCarrierConnectById();
        }
        // Fetch status details for tooltip
        fetchStatuses();
    }, [id]);

    // Helper function to safely get value with multiple fallbacks
    const getValueWithMultipleFallbacks = (data, ...keys) => {
        for (const key of keys) {
            if (data[key]) return data[key];
        }
        return '';
    };

    // Normalize a label for robust matching: trim, collapse spaces, remove punctuation, lowercase
    const normalizeLabel = (s = '') => {
        if (!s || s != 0) return '';
        try {
            return s.toString().trim().replace(/[\s\u00A0]+/g, ' ').replace(/[\p{P}$+<=>^`|~]/gu, '').toLowerCase(); // NOSONAR
        } catch (e) {
            // fallback for environments without full unicode regex support
            return s.toString().trim().replace(/\s+/g, ' ').replace(/[^\w\s]/g, '').toLowerCase(); // NOSONAR
        }
    };

    // Helper function to safely get value with fallbacks
    const getValueWithFallback = (data, primaryKey, secondaryKey = null, defaultValue = '') => {
        return data[primaryKey] || (secondaryKey ? data[secondaryKey] : null) || defaultValue;
    };

    // Helper function to map form data from API response
    const mapFormDataFromResponse = (data) => ({
        id: getValueWithFallback(data, 'id'),
        carrierConnectId: getValueWithFallback(data, 'carrierConnectNumber', 'carrierConnectId'),
        carrierStatus: getValueWithFallback(data, 'statusId', 'carrierStatus'),
        carrierId: getValueWithFallback(data, 'carrierId', 'carrierName'),
        companyId: getValueWithFallback(data, 'carrierConnectCompanyId'),
        sellerId: getValueWithFallback(data, 'carrierConnectSellerId'),
        trackingNumber: getValueWithFallback(data, 'trackingNumber'),
        subTrackingNumber: getValueWithFallback(data, 'subTrackingNumber'),
        length: getValueWithFallback(data, 'length'),
        width: getValueWithFallback(data, 'width'),
        height: getValueWithFallback(data, 'height'),
        weight: getValueWithFallback(data, 'weight'),
        dimensionUnitId: getValueWithFallback(data, 'dimensionUnitId'),
        weightUnitId: getValueWithFallback(data, 'weightUnitId'),
        notes: getValueWithFallback(data, 'notes'),
        packageCount: getValueWithFallback(data, 'packageCount'),
        shippedDate: getValueWithFallback(data, 'shippedDate'),
        companySellerId: getValueWithFallback(data, 'companySellerId'),
        companySellerName: getValueWithFallback(data, 'companySellerName'),
        carrierName: getValueWithFallback(data, 'carrierName'),
        statusName: getValueWithFallback(data, 'statusName'),
        createdDate: getValueWithFallback(data, 'createdDate'),
        postCharges: data.postCharges || [],
        warehousePackageCount: getValueWithFallback(data, 'warehousePackageCount'),
        warehouseWeight: getValueWithFallback(data, 'warehouseWeight'),
        warehouseWeightUnit: getValueWithFallback(data, 'warehouseWeightUnit'),
        warehouseLength: getValueWithFallback(data, 'warehouseLength'),
        warehouseWidth: getValueWithFallback(data, 'warehouseWidth'),
        warehouseHeight: getValueWithFallback(data, 'warehouseHeight'),
        warehouseDimensionUnit: getValueWithFallback(data, 'warehouseDimensionUnit'),
    });

    // Helper function to get package name from package object
    const getPackageNameFromObject = (packageObj, fallbackName) => {
        if (!packageObj) return fallbackName;
        return getValueWithMultipleFallbacks(packageObj, 'packageName', 'name', 'pkgType') || fallbackName;
    };

    // Helper function to resolve package name by packageId and serviceId
    const resolvePackageName = (packageId, serviceId, fallbackName = 'Your Packaging') => {
        // First try to find in the service-specific packages cache
        const servicePackages = servicePackagesCache[serviceId];
        if (servicePackages && servicePackages.length > 0) {
            const foundPackage = servicePackages.find(p => p.id == packageId);
            if (foundPackage) {
                return getPackageNameFromObject(foundPackage, fallbackName);
            }
        }
        
        // Fallback to global packages list if available
        if (packages && packages.length > 0) {
            const globalPackage = packages.find(p => p.id == packageId);
            if (globalPackage) {
                return getPackageNameFromObject(globalPackage, fallbackName);
            }
        }
        
        return fallbackName;
    };

    // Helper function to map package data from API response
    const mapPackageFromResponse = (pkg) => {
        // Enhanced mapping to handle multiple field name formats from different API responses
        const mappedPackage = {
            id: pkg.id,
            carrierConnectPackageNumber: pkg.carrierConnectPackageNumber,
            pkgType: pkg.packageType,
            trackingNumber: pkg.trackingNumber,
            length: getValueWithFallback(pkg, 'length'),
            width: getValueWithFallback(pkg, 'width'),
            height: getValueWithFallback(pkg, 'height'),
            weight: getValueWithFallback(pkg, 'weight'),
            dimensionUnitId: getValueWithFallback(pkg, 'dimensionUnitId'),
            weightUnitId: getValueWithFallback(pkg, 'weightUnitId'),
            packageCount: getValueWithFallback(pkg, 'packageCount'), // Package count at package level
            // Map carrier fields - handle both original and updated response formats
            carrierCompanyId: parseInt(getValueWithFallback(pkg, 'carrierCompanyId', 'carrierCompany_Id')) || 0, // NOSONAR
            serviceId: parseInt(getValueWithFallback(pkg, 'serviceId', 'service_Id')) || 0, // NOSONAR
            packageId: parseInt(getValueWithFallback(pkg, 'packageId', 'package_Id')) || 0, // NOSONAR
            packageType: getValueWithFallback(pkg, 'packageType', 'pkgType'),
            carrierName: getValueWithFallback(pkg, 'carrierName', 'carrierCompanyName'),
            serviceName: getValueWithFallback(pkg, 'serviceName', 'serviceTypeName'),
            packageTypeName: getValueWithMultipleFallbacks(pkg, 'packageTypeName', 'packageName', 'pkgType'),
            images: pkg.packageImages || [],
            attachments: pkg.packageAttachments || [],
            notes: getValueWithFallback(pkg, 'notes'),
            createdDate: getValueWithFallback(pkg, 'createdDate')
        };

        // If names are missing, try to resolve them from Redux store data
        if (!mappedPackage.carrierName && mappedPackage.carrierCompanyId && services) {
            const carrier = services.find(s => s.carrierCompanyId == mappedPackage.carrierCompanyId);
            if (carrier) {
                mappedPackage.carrierName = carrier.carrierCompanyName;
            }
        }

        if (!mappedPackage.serviceName && mappedPackage.serviceId && services) {
            const carrier = services.find(s => s.carrierCompanyId == mappedPackage.carrierCompanyId);
            if (carrier?.services) {
                const foundService = carrier.services.find(service => service.id == mappedPackage.serviceId);
                if (foundService) {
                    mappedPackage.serviceName = foundService.serviceName || foundService.name;
                }
            }
        }

        return mappedPackage;
    };

    // Handler to accept parsed CSV rows from bulk upload dialog and add to packagesList
    const handleAddPackagesFromCsv = (rows = []) => {
        if (!rows?.length) return;
        // If rows already contain numeric ID fields (carrierCompanyId/serviceId/packageId), append directly
        const first = rows[0];
        const hasIdFields = (obj) => obj && (obj.hasOwnProperty('carrierCompanyId') || obj.hasOwnProperty('serviceId') || obj.hasOwnProperty('packageId'));
        if (hasIdFields(first)) {
            setPackagesList(prev => [...prev, ...rows]);
            return;
        }

        // Map CSV row fields to package object expected by packagesList using shared mapper
        const mapped = rows.map((row, idx) => {
            const { mappedPkg } = mapCsvRow(row, servicePackagesCache);
            return mappedPkg;
        });

        // Append to existing packagesList
        setPackagesList(prev => [...prev, ...mapped]);
    };

    // Validate and map CSV rows: ensure Carrier/Service/Package text values match dropdowns
    // Async validation that ensures package lists are loaded for CSV rows' services before mapping

    // Helper function to check for duplicate tracking numbers
    const checkDuplicateTracking = (row, existingTrackingSet) => {
        const rawTracking = (getValueFromRow(row, ['TrackingNumber','trackingNumber']) || '').toString().trim();
        if (rawTracking && existingTrackingSet.has(rawTracking)) {
            return `Tracking number '${rawTracking}' already exists in package details`;
        }
        return null;
    };

    // Helper function to resolve carrier ID from raw carrier data
    const resolveCarrierForRow = (rawCarrier, services) => {
        if (!rawCarrier) return '';

        const asId = parseInt(rawCarrier); // NOSONAR
        if (!isNaN(asId) && services?.length) { // NOSONAR
            const byId = services.find(c => parseInt(c.carrierCompanyId) === asId); // NOSONAR
            if (byId) return asId;
        }

        if (services?.length) {
            const nl = normalizeLabel(rawCarrier);
            const found = services.find(c => normalizeLabel((c.carrierCompanyName || c.carrierName || c.name) || '') === nl);
            if (found) return found.carrierCompanyId;
        }

        return '';
    };

    // Helper function to resolve service ID from raw service data for CSV processing
    const resolveServiceForRow = (rawService, resolvedCarrierId, services) => {
        if (!rawService) return '';

        const asId = parseInt(rawService); // NOSONAR
        if (!isNaN(asId)) { // NOSONAR
            return asId;
        }

        if (!services?.length) {
            return '';
        }

        let foundService = null;

        if (resolvedCarrierId) {
            const carrierEntry = services.find(s => parseInt(s.carrierCompanyId) === parseInt(resolvedCarrierId)); // NOSONAR
            foundService = searchServiceInCarrier(carrierEntry, rawService);
        } else {
            foundService = searchServiceInAllCarriers(services, rawService);
        }

        return foundService ? foundService.id : '';
    };

    // Helper function to normalize package response data
    const normalizePackagesResponse = (resPkgsRaw) => {
        try {
            if (!resPkgsRaw) {
                return [];
            } else if (Array.isArray(resPkgsRaw)) {
                return resPkgsRaw;
            } else if (resPkgsRaw.data) {
                return resPkgsRaw.data.EntityList || resPkgsRaw.data.entityList || resPkgsRaw.data || [];
            } else if (resPkgsRaw.payload) {
                return resPkgsRaw.payload;
            } else if (resPkgsRaw.EntityList) {
                return resPkgsRaw.EntityList;
            } else {
                return resPkgsRaw.length ? Array.from(resPkgsRaw) : [];
            }
        } catch (normErr) {
            console.error('[CSV Validate] error normalizing packages response', normErr, resPkgsRaw);
            return [];
        }
    };

    // Helper function to fetch packages for a specific service
    const fetchPackagesForService = async (resolvedServiceId, localServicePackages) => {
        if (!resolvedServiceId) return;

        const svcIdNum = Number(resolvedServiceId);
        if (!servicePackagesCache?.[svcIdNum]) {
            try {
                console.debug('[CSV Validate] fetching packages for serviceId=', svcIdNum);
                const resPkgsRaw = await Promise.resolve(dispatch(getAllPackagesByServiceId(svcIdNum)));
                const resPkgs = normalizePackagesResponse(resPkgsRaw);

                console.debug('[CSV Validate] fetched packages normalized count=', (resPkgs || []).length, 'sample=', (resPkgs || []).slice(0,5));
                if (resPkgs?.length) {
                    setServicePackagesCache(prev => ({ ...prev, [svcIdNum]: resPkgs }));
                    localServicePackages[svcIdNum] = resPkgs;
                }
            } catch (e) {
                console.error('[CSV Validate] error fetching packages for serviceId=', resolvedServiceId, e);
            }
        }
    };

    // Helper function to ensure unit lists are available
    const ensureUnitListsAvailable = async () => {
        try {
            if ((!weightUnitList || weightUnitList.length === 0) || (!dimensionUnitList || dimensionUnitList.length === 0)) {
                console.debug('[CSV Validate] fetching unit lists');
                const unitPromises = [];
                if (!weightUnitList || weightUnitList.length === 0) unitPromises.push(dispatch(getWeightUnitList()));
                if (!dimensionUnitList || dimensionUnitList.length === 0) unitPromises.push(dispatch(getDimensionUnitList()));
                const results = await Promise.all(unitPromises); // NOSONAR
                console.debug('[CSV Validate] fetched unit lists results=', results?.length);
            }
        } catch (e) {
            console.error('[CSV Validate] error fetching unit lists', e);
        }
    };

    // Helper function to process a single CSV row
    const processCsvRow = async (row, rowIndex, existingTrackingSet, localServicePackages) => {
        const rawCarrier = (getValueFromRow(row, ['Carrier*','Carrier']) || '').toString().trim();
        const rawService = (getValueFromRow(row, ['Service*','Service']) || '').toString().trim();

        // Check for duplicate tracking numbers
        const duplicateError = checkDuplicateTracking(row, existingTrackingSet);
        if (duplicateError) {
            return { error: { row: rowIndex, messages: [duplicateError] } };
        }

        // Resolve carrier and service
        const resolvedCarrierId = resolveCarrierForRow(rawCarrier, services);
        const resolvedServiceId = resolveServiceForRow(rawService, resolvedCarrierId, services);

        // Fetch packages for service if needed
        await fetchPackagesForService(resolvedServiceId, localServicePackages);

        // Ensure unit lists are available
        await ensureUnitListsAvailable();

        // Run the full mapper with updated caches
        const { mappedPkg, messages } = mapCsvRow(row, localServicePackages);
        if (messages?.length) {
            return { error: { row: rowIndex, messages } };
        } else {
            return { mapped: mappedPkg };
        }
    };

    const validateAndMapRows = async (rows = []) => {
        const rowErrors = [];
        const mapped = [];
        const localServicePackages = { ...(servicePackagesCache || {}) };

        // Build a set of existing tracking numbers for duplicate checks
        const existingTrackingSet = new Set((packagesList || []).map(p => (p?.trackingNumber) ? p.trackingNumber.toString().trim() : '').filter(Boolean));

        for (let idx = 0; idx < rows.length; idx++) {
            const row = rows[idx];
            const rowIndex = idx + 2;

            const result = await processCsvRow(row, rowIndex, existingTrackingSet, localServicePackages);
            if (result.error) {
                rowErrors.push(result.error);
            } else if (result.mapped) {
                mapped.push(result.mapped);
            }
        }

        return { valid: rowErrors.length === 0, mappedRows: mapped, rowErrors };
    };

    // Helper function to load packages for a service and update cache
    const loadPackagesForServiceAndUpdateCache = (serviceId) => {
        if (serviceId && !servicePackagesCache[serviceId]) {
            return dispatch(getAllPackagesByServiceId(serviceId))
                .then((packagesResult) => {
                    if (packagesResult && packagesResult.length > 0) {
                        setServicePackagesCache(prev => ({
                            ...prev,
                            [serviceId]: packagesResult
                        }));
                    }
                })
                .catch((error) => {
                    // Package loading error handled gracefully
                });
        }
        return Promise.resolve(); // Return resolved promise for already cached services
    };

    // Helper function to process packages from API response
    const processPackagesData = (packageDetails) => {
        if (packageDetails && packageDetails.length > 0) {
            const mappedPackages = packageDetails.map(mapPackageFromResponse);
            setPackagesList(mappedPackages);
            setOriginalPackagesList([...mappedPackages]); // Store original packages for update comparison
            
            // Immediately load package details for all unique services found
            const uniqueServiceIds = [...new Set(mappedPackages.map(pkg => pkg.serviceId).filter(Boolean))];
            const packageLoadPromises = uniqueServiceIds.map(loadPackagesForServiceAndUpdateCache);
            
            // Execute all package loading operations in parallel
            Promise.all(packageLoadPromises);
        }
    };

    // Helper function to process media files from API response
    const processMediaFiles = (data) => {
        if (data.images && data.images.length > 0) {
            setImage(data.images);
        }
        if (data.attachments && data.attachments.length > 0) {
            setAttachmentFile(data.attachments);
        }
    };

    // Helper function to handle API response success
    const handleSuccessResponse = (data) => {
        setFormData(mapFormDataFromResponse(data));
        processPackagesData(data.packageDetails);
        processMediaFiles(data);
        setErrors({}); // Clear any previous errors when loading new data
        // Store the original status for In Transit restrictions
        setOriginalStatus(getValueWithFallback(data, 'statusId', 'carrierStatus'));
    };

    // Helper function to handle API response errors
    const handleResponseError = (response) => {
        const errorMessage = response?.message || 'Unable to load carrier connect details. The record may not exist or you may not have permission to view it.';
        toast.error(errorMessage);
    };

    // Helper function to handle catch block errors
    const handleCatchError = (error) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred while loading the carrier connect details. Please try again later.';
        toast.error(errorMessage);
    };

    const fetchCarrierConnectById = async () => {
        if (!id || id === '0') return;
        
        setLoader(true);
        try {
            dispatch(getCarrierConnectById(id))
                .then(response => {
                    if (response?.success && response.payload) {
                        handleSuccessResponse(response.payload);
                    } else {
                        handleResponseError(response);
                    }
                })
                .catch(error => {
                    handleCatchError(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        } catch (error) {
            handleCatchError(error);
            setLoader(false);
        }
    };

    const handleFormValue = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setErrors(prev => ({ ...prev, [name]: '' })); // Clear field error when user makes changes
    };

    const handleCompanyChange = (selectedOption) => {
        const companyId = selectedOption ? selectedOption.value : '';
        setFormData(prev => ({
            ...prev,
            companyId: companyId,
            sellerId: '' // Clear seller when company changes
        }));
        setSellerList([]); // Clear seller list immediately
        setErrors(prev => ({ ...prev, companyId: '', sellerId: '' }));

        // Fetch sellers for the selected company
        if (companyId && userDetails?.Value?.isSuperAdmin) {
            fetchSellers(companyId);
        }
    };

    const handleSellerChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            sellerId: selectedOption ? selectedOption.value : ''
        }));
        setErrors(prev => ({ ...prev, sellerId: '' }));
    };

    // Helper function to validate required basic fields
    const validateBasicFields = (error) => {
        let isValid = true;

        if (!formData.carrierId || formData.carrierId === '') {
            error.carrierId = 'Carrier is required';
            isValid = false;
        }

        if (!formData?.carrierStatus?.toString().trim()) {
            error.carrierStatus = 'Carrier Status is required';
            isValid = false;
        }

        if (!formData?.carrierConnectId?.toString().trim()) {
            error.carrierConnectId = 'Carrier Connect ID is required';
            isValid = false;
        }

        // Validate company and seller for super admin users
        if (userDetails?.Value?.isSuperAdmin) {
            if (!formData.companyId || formData.companyId === '') {
                error.companyId = 'Company is required';
                isValid = false;
            }

            if (!formData.sellerId || formData.sellerId === '') {
                error.sellerId = 'Seller is required';
                isValid = false;
            }
        }

        return isValid;
    };

    // Helper function to validate dimensions and units
    const validateDimensionsAndUnits = (error) => {
        let isValid = true;
        const hasAnyDimension = formData.length || formData.width || formData.height;

        if (!hasAnyDimension) {
            error.dimensions = 'At least one dimension (Length, Width, or Height) is required';
            isValid = false;
        }

        if (hasAnyDimension && (!formData.dimensionUnitId || formData.dimensionUnitId === '')) {
            error.dimensionUnitId = 'Dimension Unit is required when dimensions are provided';
            isValid = false;
        }

        return isValid;
    };

    // Helper function to validate weight and weight unit
    const validateWeightFields = (error) => {
        let isValid = true;

        if (!formData.weight || formData.weight === '' || Number(formData.weight) <= 0) {
            error.weight = 'Weight is required and must be greater than 0';
            isValid = false;
        }

        if (formData.weight && (!formData.weightUnitId || formData.weightUnitId === '')) {
            error.weightUnitId = 'Weight Unit is required when weight is provided';
            isValid = false;
        }

        return isValid;
    };

    // Helper function to validate package count
    const validatePackageCount = (error) => {
        let isValid = true;

        if (formData.packageCount && Number(formData.packageCount) < 0) {
            error.packageCount = 'Package count cannot be negative';
            isValid = false;
        }

        return isValid;
    };

    // Helper function to validate seller tracking number for in transit status
    const validateSellerTrackingNumberForInTransit = (error) => {
        let isValid = true;

        // Check if user is a seller (case insensitive)
        const isSeller = (userDetails?.Value?.roleName === 'Seller' || userDetails?.Value?.roleName === 'seller') ||
                        (userDetails?.Value?.role === 'Seller' || userDetails?.Value?.role === 'seller');

        // Check if status is "In Transit"
        const isInTransit = formData.carrierStatus == CARRIER_CONNECT_STATUS.IN_TRANSIT;

        // If seller is changing status to "In Transit", tracking number is required
        if (isSeller && isInTransit) {
            if (!formData.trackingNumber || formData.trackingNumber.trim() === '') {
                error.trackingNumber = 'Tracking Number is required when changing status to "In Transit"';
                isValid = false;
            }
            // Enforce length constraints
            else if (formData.trackingNumber.toString().trim().length < TRACKING_MIN_LENGTH || formData.trackingNumber.toString().trim().length > TRACKING_MAX_LENGTH) {
                error.trackingNumber = `Tracking Number must be between ${TRACKING_MIN_LENGTH} and ${TRACKING_MAX_LENGTH} characters`;
                isValid = false;
            }
        }

        return isValid;
    };

    // Helper function to validate main form fields
    const validateMainFormFields = (error) => {
        const basicFieldsValid = validateBasicFields(error);
        const dimensionsValid = validateDimensionsAndUnits(error);
        const weightValid = validateWeightFields(error);
        const packageCountValid = validatePackageCount(error);
        const trackingNumberValid = validateSellerTrackingNumberForInTransit(error);

        // Validate subTrackingNumber length for main form when provided
        if (formData.subTrackingNumber && formData.subTrackingNumber.toString().trim().length > SUB_TRACKING_MAX_LENGTH) {
            error.subTrackingNumber = `Sub Tracking Number cannot exceed ${SUB_TRACKING_MAX_LENGTH} characters`;
            // keep trackingNumberValid as false if not already
        }

        return basicFieldsValid && dimensionsValid && weightValid && packageCountValid && trackingNumberValid;
    };

    // Helper function to validate package tracking number
    const validatePackageTrackingNumber = (pkg, index, error) => {
        let isValid = true;

        if (pkg.trackingNumber && formData.trackingNumber &&
            pkg.trackingNumber.toString().trim() === formData.trackingNumber.toString().trim()) {
            error[`package_${index}_trackingNumber`] = `Package ${index + 1} tracking number must be different from main tracking number`;
            isValid = false;
        }

        // Enforce tracking number length on package level if provided
        if (pkg.trackingNumber && (pkg.trackingNumber.toString().trim().length < TRACKING_MIN_LENGTH || pkg.trackingNumber.toString().trim().length > TRACKING_MAX_LENGTH)) {
            error[`package_${index}_trackingNumber`] = `Package ${index + 1} tracking number must be between ${TRACKING_MIN_LENGTH} and ${TRACKING_MAX_LENGTH} characters`;
            isValid = false;
        }

        // Enforce subTrackingNumber length if present on package object
        if (pkg.subTrackingNumber && pkg.subTrackingNumber.toString().trim().length > SUB_TRACKING_MAX_LENGTH) {
            error[`package_${index}_subTrackingNumber`] = `Package ${index + 1} sub tracking number cannot exceed ${SUB_TRACKING_MAX_LENGTH} characters`;
            isValid = false;
        }

        return isValid;
    };

    // Helper function to validate package required fields
    const validatePackageRequiredFields = (pkg, index, error) => {
        let isValid = true;

        if (!pkg.carrierCompanyId || pkg.carrierCompanyId === '') {
            error[`package_${index}_carrierCompanyId`] = `Carrier Company is required for Package ${index + 1}`;
            isValid = false;
        }

        if (!pkg.serviceId || pkg.serviceId === '') {
            error[`package_${index}_serviceId`] = `Service is required for Package ${index + 1}`;
            isValid = false;
        }

        if (!pkg.packageId || pkg.packageId === '') {
            error[`package_${index}_packageId`] = `Package is required for Package ${index + 1}`;
            isValid = false;
        }

        return isValid;
    };

    // Helper function to validate package weight fields
    const validatePackageWeightFields = (pkg, index, error) => {
        let isValid = true;

        if (!pkg.weight || pkg.weight === '' || Number(pkg.weight) <= 0) {
            error[`package_${index}_weight`] = `Weight is required for Package ${index + 1} and must be greater than 0`;
            isValid = false;
        }

        if (pkg.weight && (!pkg.weightUnitId || pkg.weightUnitId === '')) {
            error[`package_${index}_weightUnitId`] = `Weight Unit is required for Package ${index + 1}`;
            isValid = false;
        }

        return isValid;
    };

    // Helper function to validate package dimensions
    const validatePackageDimensions = (pkg, index, error) => {
        let isValid = true;
        const hasAnyDimension = pkg.length || pkg.width || pkg.height;

        if (!hasAnyDimension) {
            error[`package_${index}_dimensions`] = `At least one dimension (Length, Width, or Height) is required for Package ${index + 1}`;
            isValid = false;
        }

        if (hasAnyDimension && (!pkg.dimensionUnitId || pkg.dimensionUnitId === '')) {
            error[`package_${index}_dimensionUnitId`] = `Dimension Unit is required for Package ${index + 1}`;
            isValid = false;
        }

        return isValid;
    };

    // Helper function to validate individual package
    const validatePackage = (pkg, index, error) => {
        const trackingNumberValid = validatePackageTrackingNumber(pkg, index, error);
        const requiredFieldsValid = validatePackageRequiredFields(pkg, index, error);
        const weightFieldsValid = validatePackageWeightFields(pkg, index, error);
        const dimensionsValid = validatePackageDimensions(pkg, index, error);

        return trackingNumberValid && requiredFieldsValid && weightFieldsValid && dimensionsValid;
    };

    // Helper function to validate all packages
    const validatePackages = (error) => {
        let isValid = true;

        if (packagesList.length > 0) {
            packagesList.forEach((pkg, index) => {
                const packageIsValid = validatePackage(pkg, index, error);
                if (!packageIsValid) {
                    isValid = false;
                }
            });
        }

        return isValid;
    };

    // Helper function to handle validation errors and show toast
    const handleValidationErrors = (error, isFormValid) => {
        setErrors(error);
        
        if (!isFormValid) {
            const errorMessages = Object.values(error);
            if (errorMessages.length > 0) {
                // Remove duplicate error messages to prevent double toasts
                const uniqueErrorMessages = [...new Set(errorMessages)];
                // Show the first unique error message for better user experience
                toast.error(uniqueErrorMessages[0]);
            } else {
                toast.error('Please fix the validation errors before proceeding');
            }
        }
    };

    const handleValidations = () => {
        let error = {};
        
        const mainFormIsValid = validateMainFormFields(error);
        const packagesAreValid = validatePackages(error);
        const isFormValid = mainFormIsValid && packagesAreValid;

        handleValidationErrors(error, isFormValid);
        return isFormValid;
    };

    // Helper function to handle successful update response
    // Helper function to prepare update data
    const prepareUpdateData = () => {
        // Prepare the notes field - append additional notes if provided
        let finalNotes = formData.notes || '';
        
        // If user has additional notes access, is in edit mode, status is NOT New and NOT Closed, and has entered additional notes, append them
        if (canSeeAdditionalNotes() && isEditable && !isNewStatus() && !isClosedStatus() && additionalNotes?.trim()) {
            const additionalText = additionalNotes.trim();
            if (finalNotes) {
                finalNotes += '\n' + ADDITIONAL_NOTES_PREFIX + additionalText;
            } else {
                finalNotes = ADDITIONAL_NOTES_PREFIX + additionalText;
            }
        }

        return {
            ...formData,
            notes: finalNotes, // Use the final notes with appended additional notes
            id: parseInt(id), // NOSONAR
            packages: packagesList,
            originalPackages: originalPackagesList,
            tenantId: userDetails?.Value?.tenantId || '1',
            companySellerId: userDetails?.Value?.CompanyId || userDetails?.Value?.CompanySellerId || '',
            updatedBy: userDetails?.Value?.EmployeeId || userDetails?.Value?.userName || '',
            // Include company and seller IDs for super admin users
            ...(userDetails?.Value?.isSuperAdmin && {
                carrierConnectCompanyId: formData.companyId,
                carrierConnectSellerId: formData.sellerId
            })
        };
    };

    // Helper function to handle successful update response
    const handleUpdateSuccess = (updateResult) => {
        setOriginalStatus(formData.carrierStatus);
        
        // Clear additional notes after successful save (only if they were visible and used)
        if (canSeeAdditionalNotes() && isEditable && !isNewStatus() && !isClosedStatus() && additionalNotes.trim()) {
            setAdditionalNotes('');
        }
        
        if (hasMediaChanges()) {
            return handleUpdateMedia(updateResult);
        } else {
            toast.success('Carrier Connect updated successfully');
            
            // Navigate back to approval screen if edit was initiated from approval screen
            if (location?.state?.fromApprovalScreen) {
                navigate('/carrier-connect/assignment-approval');
            } else {
                navigate('/carrier-connect');
            }
            
            setLoader(false);
            return Promise.resolve();
        }
    };

    // Helper function to handle update API error response
    const handleUpdateError = (updateResult) => {
        const errorMessage = updateResult?.message || 'Failed to update carrier connect';
        toast.error(errorMessage);
        setLoader(false);
        return Promise.reject(new Error(errorMessage)); // NOSONAR
    };

    // Helper function to handle update catch errors
    const handleUpdateCatchError = (error) => {
        if (!error.message || error.message === 'An unexpected error occurred while updating carrier connect') {
            const errorMessage = error?.response?.data?.message?.appStatusDescription || 
                                error?.message || 
                                'An unexpected error occurred while updating carrier connect';
            toast.error(errorMessage);
        }
        setLoader(false);
    };

    // Helper function to handle existing carrier connect update
    const handleUpdateCarrierConnect = () => {
        try {
            setLoader(true);
            const submitData = prepareUpdateData();

            dispatch(updateCarrierConnect(submitData))
                .then(updateResult => {
                    if (updateResult?.success) {
                        return handleUpdateSuccess(updateResult);
                    } else {
                        return handleUpdateError(updateResult);
                    }
                })
                .catch(error => {
                    handleUpdateCatchError(error);
                });
        } catch (error) {
            const errorMessage = error?.response?.data?.message?.appStatusDescription || 
                                error?.message || 
                                'An unexpected error occurred while updating carrier connect';
            toast.error(errorMessage);
            setLoader(false);
        }
    };

    const handleSave = async () => {
        if (handleValidations()) {
            setLoader(true);
            
            if (Number(id) === 0) {
                handleCreateCarrierConnect();
            } else {
                handleUpdateCarrierConnect();
            }
        }
    };

    // Handle create carrier connect following inbound product pattern
    const handleCreateCarrierConnect = () => { // NOSONAR
        try {
            setLoader(true);

            // Step 1: Create carrier connect data only (no media files)
            const submitData = {
                ...formData,
                packages: packagesList,
                // Add user context information from JWT
                tenantId: userDetails?.Value?.tenantId || '1',
                companySellerId: userDetails?.Value?.CompanyId || userDetails?.Value?.CompanySellerId || '',
                createdBy: userDetails?.Value?.EmployeeId || userDetails?.Value?.userName || '',
                // Include company and seller IDs for super admin users
                ...(userDetails?.Value?.isSuperAdmin && {
                    carrierConnectCompanyId: formData.companyId,
                    carrierConnectSellerId: formData.sellerId
                })
            };

            dispatch(createCarrierConnect(submitData))
                .then(createResult => {
                    // Determine status (prefer API's Status field when available)
                    const responseStatus = createResult?.data?.Status || createResult?.status;

                    // If status is 200, proceed with success behavior and ignore any AppStatusDescription
                    if (responseStatus === 200) {
                        // Only call media upload API if there are new files to upload
                        if (hasMediaChanges()) {
                            return handleUploadMedia(createResult);
                        } else {
                            // No media to upload, complete the create process
                            toast.success('Carrier Connect created successfully');
                            navigate('/carrier-connect');
                            setLoader(false);
                            return Promise.resolve(); // NOSONAR
                        }
                    }

                    // For non-200 statuses, extract a meaningful message and show it
                    let apiMessage = null;
                    const candidateMessages = [
                        createResult?.data?.Message?.AppStatusDescription,
                        createResult?.data?.Message?.AppStatusCode,
                        createResult?.data?.Message,
                        createResult?.data?.message,
                        createResult?.data?.message?.appStatusDescription,
                        createResult?.data?.Message?.appStatusDescription,
                        createResult?.message,
                        createResult?.error?.message
                    ];

                    for (const m of candidateMessages) {
                        if (typeof m === 'string' && m.trim()) {
                            apiMessage = m.trim();
                            break;
                        }
                    }

                    const errorMessage = apiMessage || 'Failed to create carrier connect';
                    toast.error(errorMessage);
                    setLoader(false);
                    return Promise.reject(new Error(errorMessage)); // NOSONAR
                })
                .catch(error => {
                    // Only show toast error if it hasn't been shown already
                    if (!error.message || error.message === 'An unexpected error occurred while creating carrier connect') {
                        const errorMessage = error?.response?.data?.Message?.AppStatusDescription || 
                                            error?.message || 
                                            'An unexpected error occurred while creating carrier connect';
                        toast.error(errorMessage);
                    }
                    setLoader(false);
                });
        } catch (error) {
            const errorMessage = error?.response?.data?.Message?.AppStatusDescription || 
                                error?.message || 
                                'An unexpected error occurred while creating carrier connect';
            toast.error(errorMessage);
            setLoader(false);
        }
    };

    // Helper function to prepare master level media
    const prepareMasterMedia = () => {
        const masterImages = image?.filter(img => img.file).map(img => img.file) || [];
        const masterAttachments = attachmentFile?.filter(att => att.file).map(att => att.file) || [];
        
        return {
            masterImages,
            masterAttachments,
            masterImageIdsToDelete,
            masterAttachmentIdsToDelete
        };
    };

    // Helper function to prepare package media updates
    const preparePackageMediaUpdates = (createdPackages) => {
        const packageMediaUpdates = [];
        
        if (!packagesList || packagesList.length === 0) {
            return packageMediaUpdates;
        }

        packagesList.forEach((pkg, index) => {
            const packageImages = pkg.images?.filter(img => img.file).map(img => img.file) || [];
            const packageAttachments = pkg.attachments?.filter(att => att.file).map(att => att.file) || [];
            
            // Get package image and attachment IDs to delete for this specific package
            const packageImageDeletions = pkg.imageIdsToDelete || [];
            const packageAttachmentDeletions = pkg.attachmentIdsToDelete || [];
            
            if (packageImages.length > 0 || packageAttachments.length > 0 || packageImageDeletions.length > 0 || packageAttachmentDeletions.length > 0) {
                const packageId = pkg.id || (createdPackages[index]?.id);
                
                if (packageId) {
                    const packageUpdate = {
                        packageId,
                        packageImages,
                        packageAttachments
                    };
                    
                    if (packageImageDeletions.length > 0) {
                        packageUpdate.packageImageIdsToDelete = packageImageDeletions;
                    }
                    
                    if (packageAttachmentDeletions.length > 0) {
                        packageUpdate.packageAttachmentIdsToDelete = packageAttachmentDeletions;
                    }
                    
                    packageMediaUpdates.push(packageUpdate);
                }
            }
        });

        return packageMediaUpdates;
    };

    // Helper function to check if any media changes exist
    const hasMediaChanges = () => {
        // Check carrier connect level media changes - only new files with 'file' property
        const hasCarrierConnectImageChanges = image.some(img => img.file);
        const hasCarrierConnectAttachmentChanges = attachmentFile.some(att => att.file);

        // Check if there are any tracked deletions
        const hasMasterImageDeletions = masterImageIdsToDelete.length > 0;
        const hasMasterAttachmentDeletions = masterAttachmentIdsToDelete.length > 0;

        // Check package level media changes - only new files with 'file' property
        const hasPackageMediaChanges = packagesList.some(pkg => {
            // Check if new images were added to this package
            const hasNewPackageImages = pkg?.images?.some(img => img.file);

            // Check if new attachments were added to this package
            const hasNewPackageAttachments = pkg?.attachments?.some(att => att.file);
            
            // Check if this package has tracked deletions
            const hasPackageImageDeletions = pkg?.imageIdsToDelete?.length > 0;
            const hasPackageAttachmentDeletions = pkg?.attachmentIdsToDelete?.length > 0;

            return hasNewPackageImages || hasNewPackageAttachments || hasPackageImageDeletions || hasPackageAttachmentDeletions;
        });

        return hasCarrierConnectImageChanges || hasCarrierConnectAttachmentChanges || hasMasterImageDeletions || hasMasterAttachmentDeletions || hasPackageMediaChanges;
    };

    const prepareMediaData = (carrierConnectId, createdPackages) => {
        // Create FormData following the same pattern as Inventory module
        const formData = new FormData();
        
        // Add carrier connect ID
        formData.append('id', carrierConnectId);
        
        // Add master images and attachments
        const masterMedia = prepareMasterMedia();
        
        // Append master images
        if (masterMedia.masterImages && masterMedia.masterImages.length > 0) {
            masterMedia.masterImages.forEach(file => {
                formData.append('masterImages', file);
            });
        }
        
        // Append master attachments  
        if (masterMedia.masterAttachments && masterMedia.masterAttachments.length > 0) {
            masterMedia.masterAttachments.forEach(file => {
                formData.append('masterAttachments', file);
            });
        }
        
        // Add master image IDs to delete
        if (masterMedia.masterImageIdsToDelete && masterMedia.masterImageIdsToDelete.length > 0) {
            masterMedia.masterImageIdsToDelete.forEach(id => {
                formData.append('masterImageIdsToDelete', id);
            });
        }
        
        // Add master attachment IDs to delete
        if (masterMedia.masterAttachmentIdsToDelete && masterMedia.masterAttachmentIdsToDelete.length > 0) {
            masterMedia.masterAttachmentIdsToDelete.forEach(id => {
                formData.append('masterAttachmentIdsToDelete', id);
            });
        }
        
        // Add package media updates
        const packageMediaUpdates = preparePackageMediaUpdates(createdPackages);
        if (packageMediaUpdates && packageMediaUpdates.length > 0) {
            packageMediaUpdates.forEach((packageMedia, index) => {
                // Add package ID
                formData.append(`packageMediaUpdates[${index}].packageId`, packageMedia.packageId);
                
                // Add package images
                if (packageMedia.packageImages && packageMedia.packageImages.length > 0) {
                    packageMedia.packageImages.forEach(file => {
                        formData.append(`packageMediaUpdates[${index}].packageImages`, file);
                    });
                }
                
                // Add package attachments
                if (packageMedia.packageAttachments && packageMedia.packageAttachments.length > 0) {
                    packageMedia.packageAttachments.forEach(file => {
                        formData.append(`packageMediaUpdates[${index}].packageAttachments`, file);
                    });
                }
                
                // Add package image IDs to delete
                if (packageMedia.packageImageIdsToDelete && packageMedia.packageImageIdsToDelete.length > 0) {
                    packageMedia.packageImageIdsToDelete.forEach(id => {
                        formData.append(`packageMediaUpdates[${index}].packageImageIdsToDelete`, id);
                    });
                }
                
                // Add package attachment IDs to delete
                if (packageMedia.packageAttachmentIdsToDelete && packageMedia.packageAttachmentIdsToDelete.length > 0) {
                    packageMedia.packageAttachmentIdsToDelete.forEach(id => {
                        formData.append(`packageMediaUpdates[${index}].packageAttachmentIdsToDelete`, id);
                    });
                }
            });
        }
        formData.append('modifiedBy', userDetails?.Value?.employeeId || 1);
        return formData;
    };

    // Helper function to handle successful media upload
    const handleMediaUploadSuccess = () => {
        // Update original status after successful create
        setOriginalStatus(formData.carrierStatus);
        
        toast.success('Carrier Connect created successfully');
        navigate('/carrier-connect');
        setLoader(false);
        
        // Reset form state
        setImage([]);
        setAttachmentFile([]);
        setPackagesList([]);
        
        // Clear deletion tracking
        setMasterImageIdsToDelete([]);
        setMasterAttachmentIdsToDelete([]);
    };

    // Helper function to handle media upload error response
    const handleMediaUploadError = (mediaResult) => {
        const errorMessage = mediaResult?.data?.Message?.AppStatusDescription || 'Media upload failed';
        toast.error(`Carrier Connect created but ${errorMessage.toLowerCase()}`);
        navigate('/carrier-connect');
        setLoader(false);
    };

    // Helper function to handle media upload catch errors
    const handleMediaUploadCatchError = (error) => {
        const errorMessage = error?.response?.data?.Message?.AppStatusDescription || 
                            error?.message || 
                            'media upload failed';
        toast.warning(`Carrier Connect created but ${errorMessage.toLowerCase()}`);
        navigate('/carrier-connect');
        setLoader(false);
    };

    // Handle media update for edit functionality
    const handleUpdateMedia = (updateResult) => {
        try {
            // Fix: Extract ID from the correct response structure based on actual API response
            const carrierConnectId = updateResult.data?.Entity?.id || updateResult.data?.entity?.id || updateResult.data?.id || formData.id;
            const updatedPackages = updateResult.data?.Entity?.packageDetails || updateResult.data?.entity?.packageDetails || updateResult.data?.packageDetails || [];
            
            if (!carrierConnectId) {
                throw new Error('Carrier Connect ID not found for media update');
            }
            
            const mediaData = prepareMediaData(carrierConnectId, updatedPackages);
            dispatch(updateCarrierConnectMedia(mediaData))
                .then(mediaResult => {
                    if (mediaResult?.data?.Status === 200) {
                        handleMediaUpdateSuccess();
                    } else {
                        handleMediaUpdateError(mediaResult);
                    }
                })
                .catch(error => {
                    handleMediaUpdateCatchError(error);
                });
        } catch (error) {
            handleMediaUpdateCatchError(error);
        }
    };

    // Helper function to handle media update success
    const handleMediaUpdateSuccess = () => {
        // Update original status after successful update
        setOriginalStatus(formData.carrierStatus);
        
        // Clear deletion tracking after successful update
        setMasterImageIdsToDelete([]);
        setMasterAttachmentIdsToDelete([]);
        
        // Clear deletion tracking from packages
        const clearedPackages = packagesList.map(pkg => {
            const { imageIdsToDelete, attachmentIdsToDelete, ...cleanPkg } = pkg;
            return cleanPkg;
        });
        setPackagesList(clearedPackages);
        
        toast.success('Carrier Connect updated successfully');
        
        // Navigate back to approval screen if edit was initiated from approval screen
        if (location?.state?.fromApprovalScreen) {
            navigate('/carrier-connect/assignment-approval');
        } else {
            navigate('/carrier-connect');
        }
        
        setLoader(false);
    };

    // Helper function to handle media update error response
    const handleMediaUpdateError = (mediaResult) => {
        const errorMessage = mediaResult?.data?.Message?.AppStatusDescription || 'Media update failed';
        toast.error(`Carrier Connect updated but ${errorMessage.toLowerCase()}`);
        
        // Navigate back to approval screen if edit was initiated from approval screen
        if (location?.state?.fromApprovalScreen) {
            navigate('/carrier-connect/assignment-approval');
        } else {
            navigate('/carrier-connect');
        }
        
        setLoader(false);
    };

    // Helper function to handle media update catch errors
    const handleMediaUpdateCatchError = (error) => {
        const errorMessage = error?.response?.data?.Message?.AppStatusDescription || 
                            error?.message || 
                            'media update failed';
        toast.warning(`Carrier Connect updated but ${errorMessage.toLowerCase()}`);
        navigate('/carrier-connect');
        setLoader(false);
    };

    // NEW: Handle media upload following inbound product pattern
    const handleUploadMedia = async (createResult) => {
        try {
            const carrierConnectId = createResult.data.Entity.id;
            const createdPackages = createResult.data.Entity.packageDetails || [];
            
            const mediaData = prepareMediaData(carrierConnectId, createdPackages);
            dispatch(updateCarrierConnectMedia(mediaData))
                .then(mediaResult => {
                    if (mediaResult?.data?.Status === 200) {
                        handleMediaUploadSuccess();
                    } else {
                        handleMediaUploadError(mediaResult);
                    }
                })
                .catch(error => {
                    handleMediaUploadCatchError(error);
                });
        } catch (error) {
            handleMediaUploadCatchError(error);
        }
    };

    // Image upload handler
    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        
        // Validate that all files are images
        const validImageFiles = files.filter(file => {
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                toast.error(`${file.name} is not a valid image file. Please upload only image files.`);
            }
            return isImage;
        });
        
        if (validImageFiles.length > 0 && validImageFiles.length <= 5) {
            const newImages = validImageFiles.map((file, index) => ({
                id: Date.now() + index,
                name: file.name,
                file: file,
                url: URL.createObjectURL(file)
            }));
            setImage(prev => [...prev, ...newImages]);
        } else if (validImageFiles.length > 5) {
            toast.error('Only 5 images can be uploaded at one time');
        }
        e.target.value = '';
    };

    // Delete image handler
    const handleDeleteImage = (index) => {
        let images = [...image];
        const imageToDelete = images[index];
        
        // If it's an existing image (has an id from API), track it for deletion
        if (imageToDelete.id && !imageToDelete.file) {
            setMasterImageIdsToDelete(prev => [...prev, imageToDelete.id]);
        }
        
        images.splice(index, 1);
        setImage(images);
        
        // If we have any deletions or remaining media, call update API
        if (id && id !== '0') {
            handleMediaUpdateAfterDelete();
        }
    };

    // Attachment upload handler
    const handleAttachmentUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            const newAttachments = files.map((file, index) => ({
                id: Date.now() + index,
                name: file.name,
                file: file,
                url: URL.createObjectURL(file),
                isDeleted: false
            }));
            setAttachmentFile(prev => [...prev, ...newAttachments]);
        }
        e.target.value = '';
    };

    // Delete attachment handler
    const handleDeleteAttachment = (index) => {
        let attachments = [...attachmentFile];
        const attachmentToDelete = attachments[index];
        
        // If it's an existing attachment (has an id from API), track it for deletion
        if (attachmentToDelete.id && !attachmentToDelete.file) {
            setMasterAttachmentIdsToDelete(prev => [...prev, attachmentToDelete.id]);
        }
        
        attachments.splice(index, 1);
        setAttachmentFile(attachments);
        
        // If we have any deletions or remaining media, call update API
        if (id && id !== '0') {
            handleMediaUpdateAfterDelete();
        }
    };

    // Package image upload handler
    const handlePackageImageUpload = (e) => {
        const files = Array.from(e.target.files);
        
        // Validate that all files are images
        const validImageFiles = files.filter(file => {
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                toast.error(`${file.name} is not a valid image file. Please upload only image files.`);
            }
            return isImage;
        });
        
        if (validImageFiles.length > 0 && validImageFiles.length <= 5) {
            const newImages = validImageFiles.map((file, index) => ({
                id: Date.now() + index,
                name: file.name,
                file: file,
                url: URL.createObjectURL(file)
            }));
            setPackageImages(prev => [...prev, ...newImages]);
        } else if (validImageFiles.length > 5) {
            toast.error('Only 5 images can be uploaded at one time');
        }
        e.target.value = '';
    };

    // Delete package image handler
    const handleDeletePackageImage = (index) => {
        let images = [...packageImages];
        const imageToDelete = images[index];
        
        // If we're editing an existing package and it's an existing image, track for deletion
        if (editingPackageIndex >= 0 && imageToDelete.id && !imageToDelete.file) {
            const updatedPackages = [...packagesList];
            const packageToUpdate = updatedPackages[editingPackageIndex];
            
            if (!packageToUpdate.imageIdsToDelete) {
                packageToUpdate.imageIdsToDelete = [];
            }
            packageToUpdate.imageIdsToDelete.push(imageToDelete.id);
            setPackagesList(updatedPackages);
            
            // Call update API for existing carrier connect
            if (id && id !== '0') {
                handleMediaUpdateAfterDelete();
            }
        }
        
        images.splice(index, 1);
        setPackageImages(images);
    };

    // Package attachment upload handler
    const handlePackageAttachmentUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            const newAttachments = files.map((file, index) => ({
                id: Date.now() + index,
                name: file.name,
                file: file,
                url: URL.createObjectURL(file),
                isDeleted: false
            }));
            setPackageAttachments(prev => [...prev, ...newAttachments]);
        }
        e.target.value = '';
    };

    // Delete package attachment handler
    const handleDeletePackageAttachment = (index) => {
        let attachments = [...packageAttachments];
        const attachmentToDelete = attachments[index];
        
        // If we're editing an existing package and it's an existing attachment, track for deletion
        if (editingPackageIndex >= 0 && attachmentToDelete.id && !attachmentToDelete.file) {
            const updatedPackages = [...packagesList];
            const packageToUpdate = updatedPackages[editingPackageIndex];
            
            if (!packageToUpdate.attachmentIdsToDelete) {
                packageToUpdate.attachmentIdsToDelete = [];
            }
            packageToUpdate.attachmentIdsToDelete.push(attachmentToDelete.id);
            setPackagesList(updatedPackages);
        }
        
        attachments.splice(index, 1);
        setPackageAttachments(attachments);
    };

    // Handle Post Charges modal
    const openChargeCarrierConnectDialog = () => {
        if (formData?.postCharges?.length > 0) {
            setShowChargeAlert(true);
        } else {
            setShowCharge(!showCharge);
        }
    };

    const handleBack = () => {
        // Navigate back to approval screen if edit/view was initiated from approval screen
        if (location?.state?.fromApprovalScreen) {
            navigate('/carrier-connect/assignment-approval');
        } else {
            navigate('/carrier-connect');
        }
    };

    // Helper function to check if the saved/original status is "In Transit"
    // This prevents editing restrictions during form editing, only applying them after save
    const isInTransitStatus = () => {
        if (originalStatus === null || originalStatus === undefined) return false;
        const originalStatusId = parseInt(originalStatus); // NOSONAR
        return originalStatusId === CARRIER_CONNECT_STATUS.IN_TRANSIT;
    };
    const isNewStatus = () => {
        if (originalStatus === null || originalStatus === undefined) return false;
        const originalStatusId = parseInt(originalStatus); // NOSONAR
        return originalStatusId === CARRIER_CONNECT_STATUS.NEW;
    };

    const isClosedStatus = () => {
        if (originalStatus === null || originalStatus === undefined) return false;
        const originalStatusId = parseInt(originalStatus); // NOSONAR
        return originalStatusId === CARRIER_CONNECT_STATUS.CLOSED;
    };

    // Initialize react-tooltip (no additional setup needed)
    useEffect(() => {
        // react-tooltip handles initialization automatically
    }, []);

    const shouldDisableComponent = () => {
        // Warehouse roles cannot create new carrier connects
        const isWarehouseCreating = Number(id) === 0 && isWarehouseRole();
        
        // Warehouse roles cannot edit carrier connects with "New" or "Closed" status
        const isWarehouseBlockedStatus = isWarehouseRole() && (isNewStatus() || isClosedStatus());
        
        // Non-warehouse, non-super admin users cannot edit "In Transit" status
        // (Warehouse roles and Super admins can edit In Transit)
        const isInTransitForNonSuperAdminNonWarehouse = isInTransitStatus() && !userDetails?.Value?.isSuperAdmin && !isWarehouseRole();

        // Seller and Admin users: cannot edit carrier connects whose original/saved status is not "New"
        const roleName = (userDetails?.Value?.roleName || '').toString().trim().toLowerCase();
        const role = (userDetails?.Value?.role || '').toString().trim().toLowerCase();
        const isSellerOrAdmin = roleName === 'seller' || role === 'seller' || roleName === 'admin' || role === 'admin';

        const originalStatusId = (originalStatus === null || originalStatus === undefined) ? null : parseInt(originalStatus); // NOSONAR
        const isOriginalNotNew = originalStatusId !== null && originalStatusId !== CARRIER_CONNECT_STATUS.NEW;
        const sellerAdminBlocked = isSellerOrAdmin && isOriginalNotNew;

        return loader || !isEditable || isInTransitForNonSuperAdminNonWarehouse || sellerAdminBlocked || isWarehouseCreating || isWarehouseBlockedStatus;
    };

    // Special function for main notes field - should be read-only for roles with additional notes access
    const shouldDisableMainNotes = () => {
        return shouldDisableComponent() || shouldMainNotesBeReadOnly();
    };

    const handleChargesDetails = () => {
        setShowChargesDetails(!showChargesDetails);
    };

    const handleOverrideCharge = () => {
        setShowCharge(!showCharge);
        setShowChargeAlert(false);
    };

    return ( 
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    <span>Carrier Connect {formData?.id && formData?.postCharges?.length > 0 && <button id='chargesBtn' className='sortBtn fontFix' onClick={handleChargesDetails}><i className='fa fa-info-circle' aria-hidden="true" style={{color: "#ff0000"}}></i></button>}</span>
                    <div className="top_right_button_container">
                        <div className="top_right_button_container">
                            {/* Show Post Charges only for Super Admins and existing carrier connections */}
                            {userDetails?.Value?.isSuperAdmin && Number(id) > 0 && (
                                <button
                                    className="btn cus-seconday-bg-btn top-btn"
                                    onClick={openChargeCarrierConnectDialog}
                                    disabled={loader}
                                >
                                    Post Charges
                                </button>
                            )}
                            {/* Bulk Package Upload moved next to Add Package button in Package Details section */}
                            <button
                                className="btn cus-seconday-bg-btn top-btn"
                                onClick={handleSave}
                                disabled={shouldDisableComponent()}
                            >
                                Save
                            </button>
                            <button
                                className="btn cus-seconday-bg-btn top-btn"
                                onClick={() => setIsEditable(true)}
                                disabled={loader || isEditable}
                            >
                                Edit
                            </button>
                            <button 
                                className="btn cus-primary-transparent-btn" 
                                onClick={handleBack} 
                                disabled={loader}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>
                
                {loader && <LoaderSpinner />}

                {/* In Transit Status Alert - Only show in edit mode for non-super admin */}
                {isInTransitStatus() && isEditable && !userDetails?.Value?.isSuperAdmin && !isWarehouseRole() && (
                    <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
                        <i className="fa fa-info-circle me-2 pr-1" aria-hidden="true"></i>
                        <div>
                            <strong>Editing Disabled:</strong> This carrier connect is currently "In Transit" and cannot be modified.
                        </div>
                    </div>
                )}

                <div className="cus-page-content-sec">
                    <div className='row equalHeight'>
                        <div className='col-12'>
                            <div className="incoming_wrapper main_content_table addressDetailWrapper addressForm fontFix">
                                {/* Basic Information Section - First Row */}
                                <div className='row mb-3'>
                                    <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="carrierConnectId">Carrier Connect ID <span className='carrier-connect-require-field'>*</span></label>
                                        <input
                                            id="carrierConnectId"
                                            className="form-control"
                                            type="text"
                                            name="carrierConnectId"
                                            value={formData.carrierConnectId}
                                            onChange={handleFormValue}
                                            disabled={!isEditable || Number(id) >= 0}
                                        />
                                        <div className='carrier-connect-error'>{errors.carrierConnectId}</div>
                                    </div>

                                    {/* Company and Seller dropdowns - visible only to super admin users */}
                                    {userDetails?.Value?.isSuperAdmin && (
                                        <>
                                            <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                                <label htmlFor="companyId">Company <span className='carrier-connect-require-field'>*</span></label>
                                                <Select
                                                    closeMenuOnSelect={true}
                                                    options={companyList}
                                                    classNamePrefix="list"
                                                    id="companyId"
                                                    name="companyId"
                                                    value={companyList.find(option => option.value === formData.companyId) || null}
                                                    onChange={handleCompanyChange}
                                                    placeholder="Select Company..."
                                                    isDisabled={!isEditable || (Number(id) > 0 && formData.postCharges?.length > 0)}
                                                    styles={{
                                                        singleValue: (provided, state) => ({
                                                            ...provided,
                                                            color: state.isDisabled ? '#212529' : provided.color
                                                        }),
                                                        control: (provided, state) => ({
                                                            ...provided,
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            boxShadow: 'none',
                                                            minHeight: '28px',
                                                            fontSize: '12px',
                                                            '&:hover': {
                                                                borderColor: '#007bff'
                                                            },
                                                            ...(state.isDisabled ? { backgroundColor: '#e9ecef', opacity: 1 } : {})
                                                        }),
                                                        menu: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                            position: 'absolute',
                                                            width: '100%',
                                                            minWidth: '100%',
                                                            maxWidth: '100%'
                                                        }),
                                                        menuList: (provided) => ({
                                                            ...provided,
                                                            padding: '2px 0',
                                                            maxHeight: '200px'
                                                        }),
                                                        option: (provided, state) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            cursor: 'pointer',
                                                            backgroundColor: selectOptionBackground(state),
                                                            color: state.isSelected ? 'white' : '#495057',
                                                            '&:hover': {
                                                                backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                            }
                                                        }),
                                                        placeholder: (provided) => ({
                                                            ...provided,
                                                            whiteSpace: 'nowrap',
                                                            color: '#5f6264ff',
                                                            fontSize: '12.5px'
                                                        }),
                                                        noOptionsMessage: (provided) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            color: '#6c757d'
                                                        }),
                                                        menuPortal: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                    menuPortalTarget={document.body}
                                                />
                                                <div className='carrier-connect-error'>{errors.companyId}</div>
                                            </div>
                                            <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                                <label htmlFor="sellerId">Seller <span className='carrier-connect-require-field'>*</span></label>
                                                <Select
                                                    closeMenuOnSelect={true}
                                                    options={sellerList}
                                                    classNamePrefix="list"
                                                    id="sellerId"
                                                    name="sellerId"
                                                    value={sellerList.find(option => option.value === formData.sellerId) || null}
                                                    onChange={handleSellerChange}
                                                    placeholder="Select Seller..."
                                                    isDisabled={!isEditable || (Number(id) > 0 && formData.postCharges?.length > 0)}
                                                    styles={{
                                                        singleValue: (provided, state) => ({
                                                            ...provided,
                                                            color: state.isDisabled ? '#212529' : provided.color
                                                        }),
                                                        control: (provided, state) => ({
                                                            ...provided,
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            boxShadow: 'none',
                                                            minHeight: '28px',
                                                            fontSize: '12px',
                                                            '&:hover': {
                                                                borderColor: '#007bff'
                                                            },
                                                            ...(state.isDisabled ? { backgroundColor: '#e9ecef', opacity: 1 } : {})
                                                        }),
                                                        menu: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                            position: 'absolute',
                                                            width: '100%',
                                                            minWidth: '100%',
                                                            maxWidth: '100%'
                                                        }),
                                                        menuList: (provided) => ({
                                                            ...provided,
                                                            padding: '2px 0',
                                                            maxHeight: '200px'
                                                        }),
                                                        option: (provided, state) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            cursor: 'pointer',
                                                            backgroundColor: selectOptionBackground(state),
                                                            color: state.isSelected ? 'white' : '#495057',
                                                            '&:hover': {
                                                                backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                            }
                                                        }),
                                                        placeholder: (provided) => ({
                                                            ...provided,
                                                            whiteSpace: 'nowrap',
                                                            color: '#5f6264ff',
                                                            fontSize: '12.5px'
                                                        }),
                                                        noOptionsMessage: (provided) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            color: '#6c757d'
                                                        }),
                                                        menuPortal: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                    menuPortalTarget={document.body}
                                                />
                                                <div className='carrier-connect-error'>{errors.sellerId}</div>
                                            </div>
                                        </>
                                    )}

                                    <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="carrierStatus">
                                            Carrier Status <span className='carrier-connect-require-field'>*</span>
                                        </label>
                                        <div className="carrier-status-wrapper">
                                            {isEditable ? (
                                                <>
                                                    <Select
                                                        closeMenuOnSelect={true}
                                                        options={statuses?.map((item) => {
                                                            // Determine roles (normalize and trim to be robust)
                                                            const roleName = (userDetails?.Value?.roleName || '').toString().trim().toLowerCase();
                                                            const role = (userDetails?.Value?.role || '').toString().trim().toLowerCase();
                                                            const isSeller = roleName === 'seller' || role === 'seller';
                                                            const isAdmin = roleName === 'admin' || role === 'admin';

                                                            // For Seller and Admin users: only allow New and In-Transit
                                                            const restrictToNewAndInTransit = isSeller || isAdmin;

                                                            // For warehouse roles: disable New, In Transit and Closed
                                                            const isWarehouseRestricted = (roleName === 'warehouse manager' || roleName === 'warehouse worker' || role === 'warehouse manager' || role === 'warehouse worker');

                                                            return {
                                                                value: item.id,
                                                                label: item.name,
                                                                isDisabled: restrictToNewAndInTransit
                                                                    ? !(item.id === CARRIER_CONNECT_STATUS.NEW || item.id === CARRIER_CONNECT_STATUS.IN_TRANSIT)
                                                                    : (isWarehouseRestricted && (item.id === CARRIER_CONNECT_STATUS.NEW || item.id === CARRIER_CONNECT_STATUS.CLOSED))
                                                            };
                                                        })}
                                                        // Ensure react-select treats option.isDisabled as the disabling predicate
                                                        isOptionDisabled={(option) => !!option.isDisabled}
                                                        classNamePrefix="list"
                                                        id="carrierStatus"
                                                        name="carrierStatus"
                                                        value={statuses?.find(option => option.id === formData.carrierStatus) ? {
                                                            value: statuses.find(option => option.id === formData.carrierStatus).id,
                                                            label: statuses.find(option => option.id === formData.carrierStatus).name
                                                        } : null}
                                                        onChange={(selectedOption) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                carrierStatus: selectedOption ? selectedOption.value : ''
                                                            }));
                                                            setErrors(prev => ({ ...prev, carrierStatus: '' }));
                                                        }}
                                                        placeholder="Select Status"
                                                        isDisabled={shouldDisableComponent()}
                                                        styles={{
                                                            singleValue: (provided, state) => ({
                                                            ...provided,
                                                            color: state.isDisabled ? '#212529' : provided.color
                                                        }),
                                                        control: (provided, state) => ({
                                                                ...provided,
                                                                borderRadius: '4px',
                                                                border: '1px solid #ced4da',
                                                                boxShadow: 'none',
                                                                minHeight: '28px',
                                                                fontSize: '12px',
                                                                '&:hover': {
                                                                    borderColor: '#007bff'
                                                                },
                                                                ...(state.isDisabled ? { backgroundColor: '#e9ecef', opacity: 1 } : {})
                                                            }),
                                                            menu: (provided) => ({
                                                                ...provided,
                                                                zIndex: 9999,
                                                                position: 'absolute',
                                                                width: '100%',
                                                                minWidth: '100%',
                                                                maxWidth: '100%'
                                                            }),
                                                            menuList: (provided) => ({
                                                                ...provided,
                                                                padding: '2px 0',
                                                                maxHeight: '200px'
                                                            }),
                                                            option: makeOptionStyle(selectOptionBackground),
                                                                placeholder: (provided) => ({
                                                                ...provided,
                                                                whiteSpace: 'nowrap',
                                                                color: '#5f6264ff',
                                                                fontSize: '12.5px'
                                                            }),
                                                            noOptionsMessage: (provided) => ({
                                                                ...provided,
                                                                padding: '8px 12px',
                                                                fontSize: '12px',
                                                                color: '#6c757d'
                                                            }),
                                                            menuPortal: (provided) => ({
                                                                ...provided,
                                                                zIndex: 9999
                                                            })
                                                        }}
                                                    />
                                                    <div className='carrier-connect-error'>{errors.carrierStatus}</div>
                                                </>
                                            ) : (
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    value={formData.statusName || 'N/A'}
                                                    disabled={true}
                                                    readOnly
                                                />
                                            )}
                                        </div>
                                    </div>
                                

                                    <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="carrierId">Carrier <span className='carrier-connect-require-field'>*</span></label>
                                        {isEditable ? (
                                            <>
                                                <Select
                                                    closeMenuOnSelect={true}
                                                    options={carriersData?.map((carrier) => ({
                                                        value: carrier.id,
                                                        label: carrier.carrierName
                                                    }))}
                                                    classNamePrefix="list"
                                                    id="carrierId"
                                                    name="carrierId"
                                                    value={carriersData?.find(option => option.id === formData.carrierId) ? {
                                                        value: carriersData.find(option => option.id === formData.carrierId).id,
                                                        label: carriersData.find(option => option.id === formData.carrierId).carrierName
                                                    } : null}
                                                    onChange={(selectedOption) => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            carrierId: selectedOption ? selectedOption.value : ''
                                                        }));
                                                        setErrors(prev => ({ ...prev, carrierId: '' }));
                                                    }}
                                                    placeholder="Select Carrier"
                                                    isDisabled={shouldDisableComponent()}
                                                    styles={{
                                                        singleValue: (provided, state) => ({
                                                            ...provided,
                                                            color: state.isDisabled ? '#212529' : provided.color
                                                        }),
                                                        control: (provided, state) => ({
                                                            ...provided,
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            boxShadow: 'none',
                                                            minHeight: '28px',
                                                            fontSize: '12px',
                                                            '&:hover': {
                                                                borderColor: '#007bff'
                                                            },
                                                            ...(state.isDisabled ? { backgroundColor: '#e9ecef', opacity: 1 } : {})
                                                        }),
                                                        menu: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                            position: 'absolute',
                                                            width: '100%',
                                                            minWidth: '100%',
                                                            maxWidth: '100%'
                                                        }),
                                                        menuList: (provided) => ({
                                                            ...provided,
                                                            padding: '2px 0',
                                                            maxHeight: '200px'
                                                        }),
                                                        option: (provided, state) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            cursor: 'pointer',
                                                            backgroundColor: selectOptionBackground(state),
                                                            color: state.isSelected ? 'white' : '#495057',
                                                            '&:hover': {
                                                                backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                            }
                                                        }),
                                                        placeholder: (provided) => ({
                                                            ...provided,
                                                            whiteSpace: 'nowrap',
                                                            color: '#5f6264ff',
                                                            fontSize: '12.5px'
                                                        }),
                                                        noOptionsMessage: (provided) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            color: '#6c757d'
                                                        }),
                                                        menuPortal: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                    menuPortalTarget={document.body}
                                                />
                                                <div className='carrier-connect-error'>{errors.carrierId}</div>
                                            </>
                                        ) : (
                                            <input
                                                className="form-control"
                                                type="text"
                                                value={formData.carrierName || 'N/A'}
                                                disabled={true}
                                                readOnly
                                            />
                                        )}
                                    </div>

                                {!userDetails?.Value?.isSuperAdmin && (
                                    <div className='col-lg-3 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="trackingNumber">Tracking Number</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            id="trackingNumber"
                                            name="trackingNumber"
                                            value={formData.trackingNumber}
                                            onChange={handleFormValue}
                                            disabled={shouldDisableComponent()}
                                            placeholder="Enter tracking number"
                                        />
                                        {errors.trackingNumber &&
                                            <div className='carrier-connect-error'>{errors.trackingNumber}</div>
                                        }
                                    </div>
                                )}

                                {userDetails?.Value?.isSuperAdmin && (
                                    <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="trackingNumber">Tracking Number</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            id="trackingNumber"
                                            name="trackingNumber"
                                            value={formData.trackingNumber}
                                            onChange={handleFormValue}
                                            disabled={shouldDisableComponent()}
                                            placeholder="Enter tracking number"
                                        />
                                        {errors.trackingNumber &&
                                            <div className='carrier-connect-error'>{errors.trackingNumber}</div>
                                        }
                                    </div>
                                )}

                                    {/* Sub Tracking Number - shown in first row for non-super admin users */}
                                    {!userDetails?.Value?.isSuperAdmin && (
                                        <div className='col-lg-3 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                            <label htmlFor="subTrackingNumber">Sub Tracking Number</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                id="subTrackingNumber"
                                                name="subTrackingNumber"
                                                value={formData.subTrackingNumber}
                                                onChange={handleFormValue}
                                                disabled={shouldDisableComponent()}
                                                placeholder="Sub tracking number"
                                            />
                                        </div>
                                    )}

                                </div>

                                {/* Tracking Information Section - Second Row */}
                                <div className='row mb-3'>
                                    {/* Sub Tracking Number - only shown for super admin users in second row */}
                                    {userDetails?.Value?.isSuperAdmin && (
                                        <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                            <label htmlFor="subTrackingNumber">Sub Tracking Number</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                id="subTrackingNumber"
                                                name="subTrackingNumber"
                                                value={formData.subTrackingNumber}
                                                onChange={handleFormValue}
                                                disabled={shouldDisableComponent()}
                                                placeholder="Sub tracking number"
                                            />
                                        </div>
                                    )}

                                    {!userDetails?.Value?.isSuperAdmin && (
                                        <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                            <label htmlFor="weight">Weight <span className='carrier-connect-require-field'>*</span></label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                id="weight"
                                                name="weight"
                                                min="0"
                                                value={formData.weight}
                                                onChange={handleFormValue}
                                                disabled={shouldDisableComponent()}
                                                placeholder="0"
                                                onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                            />
                                            {errors.weight && <div className="carrier-connect-error">{errors.weight}</div>}
                                        </div>
                                    )}

                                    {userDetails?.Value?.isSuperAdmin && (
                                        <div className='col-lg-1 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                            <label htmlFor="weight">Weight <span className='carrier-connect-require-field'>*</span></label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                id="weight"
                                                name="weight"
                                                min="0"
                                                value={formData.weight}
                                                onChange={handleFormValue}
                                                disabled={shouldDisableComponent()}
                                                placeholder="0"
                                                onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                            />
                                            {errors.weight && <div className="carrier-connect-error">{errors.weight}</div>}
                                        </div>
                                    )}

                                    <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="weightUnitId">Weight Unit <span className='carrier-connect-require-field'>*</span></label>
                                        <Select
                                            closeMenuOnSelect={true}
                                            options={weightUnitList?.map((weight) => ({
                                                value: weight.id,
                                                label: weight.name
                                            }))}
                                            classNamePrefix="list"
                                            id="weightUnitId"
                                            name="weightUnitId"
                                            value={weightUnitList?.find(option => option.id === formData.weightUnitId) ? {
                                                value: weightUnitList.find(option => option.id === formData.weightUnitId).id,
                                                label: weightUnitList.find(option => option.id === formData.weightUnitId).name
                                            } : null}
                                            onChange={(selectedOption) => {
                                                const selectedValue = selectedOption ? selectedOption.value : '';
                                                // Mirror Inventory mapping: selecting weight unit should set corresponding dimension unit
                                                setFormData(prev => {
                                                    let next = { ...prev, weightUnitId: selectedValue };
                                                    try {
                                                        // If KG or GM -> set dimension to CMSEnum, otherwise set to InchEnum
                                                        if (Number(selectedValue) === WEIGHT_UNIT_KG || Number(selectedValue) === WEIGHT_UNIT_GM) {
                                                            next.dimensionUnitId = CMSEnum;
                                                        } else if (selectedValue) {
                                                            next.dimensionUnitId = InchEnum;
                                                        }
                                                    } catch (e) {
                                                        // ignore mapping errors
                                                    }
                                                    return next;
                                                });
                                                setErrors(prev => ({ ...prev, weightUnitId: '' }));
                                            }}
                                            placeholder="Select Unit"
                                            isDisabled={shouldDisableComponent()}
                                            styles={{
                                                singleValue: (provided, state) => ({
                                                    ...provided,
                                                    color: state.isDisabled ? '#212529' : provided.color
                                                }),
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    borderRadius: '4px',
                                                    border: '1px solid #ced4da',
                                                    boxShadow: 'none',
                                                    minHeight: '28px',
                                                    fontSize: '12px',
                                                    '&:hover': {
                                                        borderColor: '#007bff'
                                                    },
                                                    ...(state.isDisabled ? { backgroundColor: '#e9ecef', opacity: 1 } : {})
                                                }),
                                                menu: (provided) => ({
                                                    ...provided,
                                                    zIndex: 9999,
                                                    position: 'absolute',
                                                    width: '100%',
                                                    minWidth: '100%',
                                                    maxWidth: '100%'
                                                }),
                                                menuList: (provided) => ({
                                                    ...provided,
                                                    padding: '2px 0',
                                                    maxHeight: '200px'
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    padding: '8px 12px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: selectOptionBackground(state),
                                                    color: state.isSelected ? 'white' : '#495057',
                                                    '&:hover': {
                                                        backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                    }
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    whiteSpace: 'nowrap',
                                                    color: '#5f6264ff',
                                                    fontSize: '12.5px'
                                                }),
                                                noOptionsMessage: (provided) => ({
                                                    ...provided,
                                                    padding: '8px 12px',
                                                    fontSize: '12px',
                                                    color: '#6c757d'
                                                }),
                                                menuPortal: (provided) => ({
                                                    ...provided,
                                                    zIndex: 9999
                                                })
                                            }}
                                            menuPortalTarget={document.body}
                                        />
                                        {errors.weightUnitId && <div className="carrier-connect-error">{errors.weightUnitId}</div>}
                                    </div>

                                    {!userDetails?.Value?.isSuperAdmin && (
                                        <div className='col-lg-1 col-md-3 col-sm-6 carrier-connect-mobMb-10'>
                                            <label htmlFor="length">Length <span className='carrier-connect-require-field'>*</span></label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                id="length"
                                                name="length"
                                                min="0"
                                                value={formData.length}
                                                onChange={handleFormValue}
                                                disabled={shouldDisableComponent()}
                                                placeholder="0"
                                                onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                            />
                                            {errors.length && <div className="carrier-connect-error">{errors.length}</div>}
                                        </div>
                                    )}

                                    {userDetails?.Value?.isSuperAdmin && (
                                        <div className='col-lg-1 col-md-3 col-sm-6 carrier-connect-mobMb-10'>
                                            <label htmlFor="length">Length <span className='carrier-connect-require-field'>*</span></label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                id="length"
                                                name="length"
                                                min="0"
                                                value={formData.length}
                                                onChange={handleFormValue}
                                                disabled={shouldDisableComponent()}
                                                placeholder="0"
                                                onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                            />
                                            {errors.length && <div className="carrier-connect-error">{errors.length}</div>}
                                        </div>
                                    )}
                                    <div className='col-lg-1 col-md-3 col-sm-6 carrier-connect-mobMb-10'>
                                        <label htmlFor="width">Width <span className='carrier-connect-require-field'>*</span></label>
                                        <input
                                            className="form-control"
                                            type="number"
                                            id="width"
                                            name="width"
                                            min="0"
                                            value={formData.width}
                                            onChange={handleFormValue}
                                            disabled={shouldDisableComponent()}
                                            placeholder="0"
                                            onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                        />
                                        {errors.width && <div className="carrier-connect-error">{errors.width}</div>}
                                    </div>
                                    <div className='col-lg-1 col-md-3 col-sm-6 carrier-connect-mobMb-10'>
                                        <label htmlFor="height">Height <span className='carrier-connect-require-field'>*</span></label>
                                        <input
                                            className="form-control"
                                            type="number"
                                            id="height"
                                            name="height"
                                            min="0"
                                            value={formData.height}
                                            onChange={handleFormValue}
                                            disabled={shouldDisableComponent()}
                                            placeholder="0"
                                            onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                        />
                                        {errors.height && <div className="carrier-connect-error">{errors.height}</div>}
                                    </div>
                                    <div className='col-lg-2 col-md-3 col-sm-6 carrier-connect-mobMb-10'>
                                        <label htmlFor="dimensionUnitId">Dimension Unit <span className='carrier-connect-require-field'>*</span></label>
                                        <Select
                                            closeMenuOnSelect={true}
                                            options={dimensionUnitList?.map((dimension) => ({
                                                value: dimension.id,
                                                label: dimension.name
                                            }))}
                                            classNamePrefix="list"
                                            id="dimensionUnitId"
                                            name="dimensionUnitId"
                                            value={dimensionUnitList?.find(option => option.id === formData.dimensionUnitId) ? {
                                                value: dimensionUnitList.find(option => option.id === formData.dimensionUnitId).id,
                                                label: dimensionUnitList.find(option => option.id === formData.dimensionUnitId).name
                                            } : null}
                                            onChange={(selectedOption) => {
                                                const selectedValue = selectedOption ? selectedOption.value : '';
                                                setFormData(prev => {
                                                    let next = { ...prev, dimensionUnitId: selectedValue };
                                                    try {
                                                        // If InchEnum -> set weight unit to LB, otherwise set to KG
                                                        if (Number(selectedValue) === InchEnum) {
                                                            next.weightUnitId = WEIGHT_UNIT_LB;
                                                        } else if (selectedValue) {
                                                            next.weightUnitId = WEIGHT_UNIT_KG;
                                                        }
                                                    } catch (e) {
                                                        // ignore mapping errors
                                                    }
                                                    return next;
                                                });
                                                setErrors(prev => ({ ...prev, dimensionUnitId: '' }));
                                            }}
                                            placeholder="Select Unit"
                                            isDisabled={shouldDisableComponent()}
                                            styles={{
                                                singleValue: (provided, state) => ({
                                                    ...provided,
                                                    color: state.isDisabled ? '#212529' : provided.color
                                                }),
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    borderRadius: '4px',
                                                    border: '1px solid #ced4da',
                                                    boxShadow: 'none',
                                                    minHeight: '28px',
                                                    fontSize: '12px',
                                                    '&:hover': {
                                                        borderColor: '#007bff'
                                                    },
                                                    ...(state.isDisabled ? { backgroundColor: '#e9ecef', opacity: 1 } : {})
                                                }),
                                                menu: (provided) => ({
                                                    ...provided,
                                                    zIndex: 9999,
                                                    position: 'absolute',
                                                    width: '100%',
                                                    minWidth: '100%',
                                                    maxWidth: '100%'
                                                }),
                                                menuList: (provided) => ({
                                                    ...provided,
                                                    padding: '2px 0',
                                                    maxHeight: '200px'
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    padding: '8px 12px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: selectOptionBackground(state),
                                                    color: state.isSelected ? 'white' : '#495057',
                                                    '&:hover': {
                                                        backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                    }
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    whiteSpace: 'nowrap',
                                                    color: '#5f6264ff',
                                                    fontSize: '12.5px'
                                                }),
                                                noOptionsMessage: (provided) => ({
                                                    ...provided,
                                                    padding: '8px 12px',
                                                    fontSize: '12px',
                                                    color: '#6c757d'
                                                }),
                                                menuPortal: (provided) => ({
                                                    ...provided,
                                                    zIndex: 9999
                                                })
                                            }}
                                            menuPortalTarget={document.body}
                                        />
                                        {errors.dimensionUnitId && <div className="carrier-connect-error">{errors.dimensionUnitId}</div>}
                                    </div>
                                    {!userDetails?.Value?.isSuperAdmin && (
                                    <div className='col-lg-3 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="packageCount">Package Count <i className="fa fa-info-circle text-info tooltipWidth"
                                            id="package-count-tooltip-icon"></i></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="packageCount"
                                            name="packageCount"
                                            value={formData.packageCount}
                                            onChange={handleFormValue}
                                            disabled={shouldDisableComponent()}
                                            placeholder="Enter count"
                                            min="0"
                                        />
                                        {errors.packageCount && <div className="carrier-connect-error">{errors.packageCount}</div>}
                                    </div>
                                    )}

                                    {userDetails?.Value?.isSuperAdmin && (
                                    <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="packageCount">Package Count <i className="fa fa-info-circle text-info tooltipWidth"
                                            id="package-count-tooltip-icon"></i></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="packageCount"
                                            name="packageCount"
                                            value={formData.packageCount}
                                            onChange={handleFormValue}
                                            disabled={shouldDisableComponent()}
                                            placeholder="Enter count"
                                            min="0"
                                        />
                                        {errors.packageCount && <div className="carrier-connect-error">{errors.packageCount}</div>}
                                    </div>
                                    )}
                                </div>
                                <div className='row mb-3 align-items-end'>
                                {/* Created Date - shown for existing records */}
                                {Number(id) > 0 && formData.createdDate && (
                                    <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="createdDate">Created Date</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            id="createdDate"
                                            value={formData.createdDate ? new Date(formData.createdDate).toLocaleString() : 'N/A'}
                                            disabled={true}
                                        />
                                    </div>
                                )}
                                {Number(id) > 0 && (formData.warehouseWeight > 0 || formData.warehouseWeightUnit > 0 || formData.warehouseLength > 0 || formData.warehouseWidth > 0 || formData.warehouseHeight > 0 || formData.warehouseDimensionUnit > 0) && (
                                    <>
                                    <div className='col-lg-1 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="warehouseWeight">Warehouse Weight</label>
                                        <input
                                            className="form-control"
                                            type="number"
                                            id="warehouseWeight"
                                            name="warehouseWeight"
                                            min="0"
                                            value={formData.warehouseWeight}
                                            disabled={true}
                                            placeholder="0"
                                            onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                        />
                                    </div>
                                    <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="warehouseWeightUnit">Warehouse Weight Unit</label>
                                        <Select
                                            closeMenuOnSelect={true}
                                            options={weightUnitList?.map((weight) => ({
                                                value: weight.id,
                                                label: weight.name
                                            }))}
                                            classNamePrefix="list"
                                            id="warehouseWeightUnit"
                                            name="warehouseWeightUnit"
                                            value={weightUnitList?.find(option => option.id === formData.warehouseWeightUnit) ? {
                                                value: weightUnitList.find(option => option.id === formData.warehouseWeightUnit).id,
                                                label: weightUnitList.find(option => option.id === formData.warehouseWeightUnit).name
                                            } : null}
                                            placeholder="Select Unit"
                                            isDisabled={true}
                                            styles={{
                                                singleValue: (provided, state) => ({
                                                            ...provided,
                                                            color: state.isDisabled ? '#212529' : provided.color
                                                }),
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    borderRadius: '4px',
                                                    border: '1px solid #ced4da',
                                                    boxShadow: 'none',
                                                    minHeight: '28px',
                                                    fontSize: '12px',
                                                    '&:hover': {
                                                        borderColor: '#007bff'
                                                    },
                                                    ...(state.isDisabled ? { backgroundColor: '#e9ecef', opacity: 1 } : {})
                                                }),
                                                menu: (provided) => ({
                                                    ...provided,
                                                    zIndex: 9999,
                                                    position: 'absolute',
                                                    width: '100%',
                                                    minWidth: '100%',
                                                    maxWidth: '100%'
                                                }),
                                                menuList: (provided) => ({
                                                    ...provided,
                                                    padding: '2px 0',
                                                    maxHeight: '200px'
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    padding: '8px 12px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: selectOptionBackground(state),
                                                    color: state.isSelected ? 'white' : '#495057',
                                                    '&:hover': {
                                                        backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                    }
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    whiteSpace: 'nowrap',
                                                    color: '#5f6264ff',
                                                    fontSize: '12.5px'
                                                }),
                                                noOptionsMessage: (provided) => ({
                                                    ...provided,
                                                    padding: '8px 12px',
                                                    fontSize: '12px',
                                                    color: '#6c757d'
                                                }),
                                                menuPortal: (provided) => ({
                                                    ...provided,
                                                    zIndex: 9999
                                                })
                                            }}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                    <div className='col-lg-1 col-md-3 col-sm-6 carrier-connect-mobMb-10'>
                                        <label htmlFor="warehouseLength">Warehouse Length</label>
                                        <input
                                            className="form-control"
                                            type="number"
                                            id="warehouseLength"
                                            name="warehouseLength"
                                            min="0"
                                            value={formData.warehouseLength}
                                            disabled={true}
                                            placeholder="0"
                                            onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                        />
                                    </div>
                                    <div className='col-lg-1 col-md-3 col-sm-6 carrier-connect-mobMb-10'>
                                        <label htmlFor="warehouseWidth">Warehouse Width</label>
                                        <input
                                            className="form-control"
                                            type="number"
                                            id="warehouseWidth"
                                            name="warehouseWidth"
                                            min="0"
                                            value={formData.warehouseWidth}
                                            disabled={true}
                                            placeholder="0"
                                            onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                        />
                                    </div>
                                    <div className='col-lg-1 col-md-3 col-sm-6 carrier-connect-mobMb-10'>
                                        <label htmlFor="warehouseHeight">Warehouse Height</label>
                                        <input
                                            className="form-control"
                                            type="number"
                                            id="warehouseHeight"
                                            name="warehouseHeight"
                                            min="0"
                                            value={formData.warehouseHeight}
                                            disabled={true}
                                            placeholder="0"
                                            onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                        />
                                    </div>
                                    <div className='col-lg-2 col-md-3 col-sm-6 carrier-connect-mobMb-10'>
                                        <label htmlFor="warehouseDimensionUnit">Warehouse Dimension Unit</label>
                                        <Select
                                            closeMenuOnSelect={true}
                                            options={dimensionUnitList?.map((dimension) => ({
                                                value: dimension.id,
                                                label: dimension.name
                                            }))}
                                            classNamePrefix="list"
                                            id="warehouseDimensionUnit"
                                            name="warehouseDimensionUnit"
                                            value={dimensionUnitList?.find(option => option.id === formData.warehouseDimensionUnit) ? {
                                                value: dimensionUnitList.find(option => option.id === formData.warehouseDimensionUnit).id,
                                                label: dimensionUnitList.find(option => option.id === formData.warehouseDimensionUnit).name
                                            } : null}
                                            placeholder="Select Unit"
                                            isDisabled={true}
                                            styles={{
                                                singleValue: (provided, state) => ({
                                                            ...provided,
                                                            color: state.isDisabled ? '#212529' : provided.color
                                                }),
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    borderRadius: '4px',
                                                    border: '1px solid #ced4da',
                                                    boxShadow: 'none',
                                                    minHeight: '28px',
                                                    fontSize: '12px',
                                                    '&:hover': {
                                                        borderColor: '#007bff'
                                                    },
                                                    ...(state.isDisabled ? { backgroundColor: '#e9ecef', opacity: 1 } : {})
                                                }),
                                                menu: (provided) => ({
                                                    ...provided,
                                                    zIndex: 9999,
                                                    position: 'absolute',
                                                    width: '100%',
                                                    minWidth: '100%',
                                                    maxWidth: '100%'
                                                }),
                                                menuList: (provided) => ({
                                                    ...provided,
                                                    padding: '2px 0',
                                                    maxHeight: '200px'
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    padding: '8px 12px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    backgroundColor: selectOptionBackground(state),
                                                    color: state.isSelected ? 'white' : '#495057',
                                                    '&:hover': {
                                                        backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                    }
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    whiteSpace: 'nowrap',
                                                    color: '#5f6264ff',
                                                    fontSize: '12.5px'
                                                }),
                                                noOptionsMessage: (provided) => ({
                                                    ...provided,
                                                    padding: '8px 12px',
                                                    fontSize: '12px',
                                                    color: '#6c757d'
                                                }),
                                                menuPortal: (provided) => ({
                                                    ...provided,
                                                    zIndex: 9999
                                                })
                                            }}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                    </>
                                )}
                                {Number(id) > 0 && formData.warehousePackageCount > 0 && (
                                    <div className='col-lg-2 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="warehousePackageCount">Warehouse Package Count</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="warehousePackageCount"
                                            name="warehousePackageCount"
                                            value={formData.warehousePackageCount}
                                            disabled={true}
                                            placeholder="Enter count"
                                            min="0"
                                        />
                                    </div>
                                )}
                                </div>

                                {/* Images and Attachments Row */}
                                <div className='row mb-3'>
                                    <div className='col-lg-3 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                        <label htmlFor="imageUpload">Images</label>
                                        <div className='uploadContainer'>
                                            <div className="inlineBlock fileUpload inventory">
                                                <button
                                                    disabled={shouldDisableComponent()}
                                                    type="button"
                                                    className="btn cus-seconday-bg-btn imgUploadbtn"
                                                    onClick={() => document.getElementById('imageUpload').click()}
                                                >
                                                    Upload Images
                                                </button>
                                                <input
                                                    id="imageUpload"
                                                    className="hiddenFileField"
                                                    onChange={handleImageUpload}
                                                    disabled={shouldDisableComponent()}
                                                    type="file"
                                                    multiple
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            {image.length > 0 && (
                                                <div className="row">
                                                    {image.map((item, index) => {
                                                        // Check if this is an existing image with URL or a new uploaded file
                                                        const imageUrl = item.imageUrl || item.filePath || item.url || (item.file && URL.createObjectURL(item.file));
                                                        const fileName = item.fileName || item.name || `Image ${index + 1}`;
                                                        
                                                        if (imageUrl) {
                                                            // Show thumbnail for images
                                                            return (
                                                                <div key={item?.id || index} className="col-auto mb-2">
                                                                    <div className="position-relative">
                                                                        {renderImageThumbnail({ imageUrl, fileName }, index)}
                                                                        {isEditable && !isInTransitStatus() && (
                                                                            <button
                                                                                className="btn btn-danger btn-sm position-absolute"
                                                                                style={{ top: '5px', right: '5px', padding: '2px 6px', fontSize: '10px' }}
                                                                                onClick={() => handleDeleteImage(index)}
                                                                                type="button"
                                                                                title="Delete Image"
                                                                            >
                                                                                <i className="fa fa-times" aria-hidden="true"></i>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        } else {
                                                            // Fallback to filename display for non-image files or missing URLs
                                                            return (
                                                                <div className="selectedFile mr-2" key={item?.id || index}>
                                                                    <div className="fileName">{fileName}</div>
                                                                    {isEditable && !isInTransitStatus() && (
                                                                        <button
                                                                            className="deleteImg"
                                                                            onClick={() => handleDeleteImage(index)}
                                                                            type="button"
                                                                        >
                                                                            <i className="fa fa-times" aria-hidden="true"></i>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12 carrier-connect-mobMb-10">
                                        <label htmlFor="attachmentUpload">Attachments</label>
                                        <div className='uploadContainer'>
                                            <div className="inlineBlock fileUpload inventory">
                                                <button
                                                    disabled={shouldDisableComponent()}
                                                    type="button"
                                                    className="btn cus-seconday-bg-btn"
                                                    onClick={() => document.getElementById('attachmentUpload').click()}
                                                >
                                                    Upload Attachments
                                                </button>
                                                <input
                                                    id="attachmentUpload"
                                                    className="hiddenFileField"
                                                    onChange={handleAttachmentUpload}
                                                    disabled={shouldDisableComponent()}
                                                    type="file"
                                                    multiple
                                                    style={{ display: 'none' }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            {attachmentFile.length > 0 &&
                                                attachmentFile.map((item, index) => (
                                                    <>
                                                        {!item.isDeleted && (
                                                            <div className="selectedFile mr-2" key={item?.id}>
                                                                <div className="fileName">
                                                                    {item.fileUrl ? (
                                                                        <a
                                                                            href={item.fileUrl}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            {item.name}
                                                                        </a>
                                                                    ) : (
                                                                        <>{item.name}</>
                                                                    )}
                                                                </div>
                                                                {isEditable && !isInTransitStatus() && (
                                                                    <button
                                                                        className="deleteFile"
                                                                        onClick={() => handleDeleteAttachment(index)}
                                                                        type="button"
                                                                    >
                                                                        <i className="fa fa-times" aria-hidden="true"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Package Details Section */}
                                {(showPackageForm || packagesList.length > 0) && (
                                    <div className="carrier-connect-section-header">
                                        <h5>Package Details</h5>
                                    </div>
                                )}

                                {/* Add Package Button */}
                                {!showPackageForm && packagesList.length === 0 && isEditable && (
                                    <div className='row mb-3'>
                                        <div className='col-12'>
                                                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                                                        <button
                                                            type="button"
                                                            className="btn cus-seconday-bg-btn"
                                                            onClick={handleAddPackage}
                                                            disabled={shouldDisableComponent()}
                                                            style={{display: 'inline-flex', alignItems: 'center'}}
                                                        >
                                                            <i className="fa fa-plus" style={{marginRight: '8px'}}></i>
                                                            <span>Add Package</span>
                                                        </button>
                                                        {Number(id) === 0 && checkPermission && checkPermission(FEATURE_CARRIERCONNECTMANAGEMENT, '', permssionBulkUpload, featureModule, props.employeePermissions) && (
                                                            <button
                                                                type="button"
                                                                className="btn cus-seconday-bg-btn"
                                                                onClick={() => setCsvModalShow(true)}
                                                                disabled={shouldDisableComponent()}
                                                            >
                                                                <i className="fa fa-upload" style={{marginRight: '8px'}}></i>
                                                                <span>Bulk Package Upload</span>
                                                            </button>
                                                        )}
                                                    </div>
                                        </div>
                                    </div>
                                )}

                                {/* Package List */}
                                {packagesList.length > 0 && (
                                    <div className='row mb-3'>
                                        <div className='col-12'>
                                            <h6 className="mb-3 text-primary" style={{display: 'flex', alignItems: 'center'}}>
                                                <i className="fa fa-list" style={{marginRight: '8px'}}></i>
                                                <span>Added Packages ({packagesList.length})</span>
                                            </h6>
                                            <div className="table-responsive" style={{border: '1px solid #dee2e6', borderRadius: '4px'}}>
                                                <table className="table table-striped table-bordered mb-0">
                                                    <thead>
                                                        <tr className="table-dark">
                                                            <th scope="col" style={{minWidth: '120px', backgroundColor: '#343a40', color: 'white', fontWeight: '600', border: '1px solid #454d55'}}>Tracking Number</th>
                                                            <th scope="col" style={{minWidth: '100px', backgroundColor: '#343a40', color: 'white', fontWeight: '600', border: '1px solid #454d55'}}>Package Type</th>
                                                            <th scope="col" style={{minWidth: '120px', backgroundColor: '#343a40', color: 'white', fontWeight: '600', border: '1px solid #454d55'}}>Dimensions</th>
                                                            <th scope="col" style={{minWidth: '80px', backgroundColor: '#343a40', color: 'white', fontWeight: '600', border: '1px solid #454d55'}}>Weight</th>
                                                            <th scope="col" style={{minWidth: '90px', backgroundColor: '#343a40', color: 'white', fontWeight: '600', border: '1px solid #454d55'}}>Package Count</th>
                                                            <th scope="col" style={{minWidth: '100px', backgroundColor: '#343a40', color: 'white', fontWeight: '600', border: '1px solid #454d55'}}>Carrier</th>
                                                            <th scope="col" style={{minWidth: '100px', backgroundColor: '#343a40', color: 'white', fontWeight: '600', border: '1px solid #454d55'}}>Service</th>
                                                            <th scope="col" style={{minWidth: '80px', backgroundColor: '#343a40', color: 'white', fontWeight: '600', border: '1px solid #454d55'}}>Package</th>
                                                            <th scope="col" style={{minWidth: '140px', backgroundColor: '#343a40', color: 'white', fontWeight: '600', border: '1px solid #454d55'}}>Created Date</th>
                                                            <th scope="col" style={{minWidth: '120px', backgroundColor: '#343a40', color: 'white', fontWeight: '600', border: '1px solid #454d55'}}>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {packagesList.map((pkg, index) => (
                                                            <tr key={pkg.id}>
                                                                <td style={{wordBreak: 'break-word'}}>{pkg.trackingNumber || '-'}</td>
                                                                <td style={{wordBreak: 'break-word'}}>{pkg.packageType || '-'}</td>
                                                                <td style={{fontSize: '12px'}}>
                                                                    {pkg.length && pkg.width && pkg.height ? (
                                                                        <>
                                                                            {pkg.length}×{pkg.width}×{pkg.height}
                                                                            {pkg.dimensionUnitId && (
                                                                                <span className="text-muted">
                                                                                    {' '}{dimensionUnitList.find(d => d.id == pkg.dimensionUnitId)?.name}
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    ) : '-'}
                                                                </td>
                                                                <td style={{fontSize: '12px'}}>
                                                                    {pkg.weight ? (
                                                                        <>
                                                                            {pkg.weight}
                                                                            {pkg.weightUnitId && (
                                                                                <span className="text-muted">
                                                                                    {' '}{weightUnitList.find(w => w.id == pkg.weightUnitId)?.name}
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    ) : '-'}
                                                                </td>
                                                                <td style={{fontSize: '12px', textAlign: 'center'}}>
                                                                    {pkg.packageCount || '-'}
                                                                </td>
                                                                <td style={{fontSize: '12px', wordBreak: 'break-word'}}>
                                                                    {(() => {
                                                                        const carrier = services?.find(s => s.carrierCompanyId == pkg.carrierCompanyId);
                                                                        return carrier?.carrierCompanyName || pkg.carrierName || '-';
                                                                    })()}
                                                                </td>
                                                                <td style={{fontSize: '12px', wordBreak: 'break-word'}}>
                                                                    {(() => {
                                                                        const carrier = services?.find(s => s.carrierCompanyId == pkg.carrierCompanyId);
                                                                        
                                                                        let foundService = null;
                                                                        if (carrier?.services) {
                                                                            foundService = carrier.services.find(service => 
                                                                                service.id == pkg.serviceId
                                                                            );
                                                                        }
                                                                        
                                                                        return foundService?.serviceName || foundService?.name || pkg.serviceName || '-';
                                                                    })()}
                                                                </td>
                                                                <td style={{fontSize: '12px', wordBreak: 'break-word'}}>
                                                                    {resolvePackageName(pkg.packageId, pkg.serviceId, pkg.packageTypeName || pkg.pkgType || 'Box')}
                                                                </td>
                                                                <td style={{fontSize: '12px'}}>
                                                                    {pkg.createdDate ? new Date(pkg.createdDate).toLocaleString() : '-'}
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex gap-1">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-info me-2"
                                                                            onClick={() => handleViewPackage(index)}
                                                                            title="View Package Details"
                                                                            style={{marginRight: '8px'}}
                                                                        >
                                                                            <i className="fa fa-eye"></i>
                                                                        </button>
                                                                        {isEditable && (
                                                                            <>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-primary me-2"
                                                                                    onClick={() => handleEditPackage(index)}
                                                                                    disabled={shouldDisableComponent()}
                                                                                    title="Edit Package"
                                                                                    style={{marginRight: '8px'}}
                                                                                >
                                                                                    <i className="fa fa-edit"></i>
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                    onClick={() => handleDeletePackage(index)}
                                                                                    disabled={shouldDisableComponent()}
                                                                                    title="Delete Package"
                                                                                >
                                                                                    <i className="fa fa-trash"></i>
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            
                                            {/* Add Another Package Button - Separate from table */}
                                            {!showPackageForm && isEditable && (
                                                <div className="mt-3 d-flex align-items-center" style={{ gap: 12 }}>
                                                    <button
                                                        type="button"
                                                        className="btn cus-seconday-bg-btn"
                                                        onClick={handleAddPackage}
                                                        disabled={!isEditable}
                                                        style={{display: 'inline-flex', alignItems: 'center'}}
                                                    >
                                                        <i className="fa fa-plus" style={{marginRight: '8px'}}></i>
                                                        <span>Add Another Package</span>
                                                    </button>

                                                    {/* Bulk Package Upload - opens CSV modal to add packages in bulk */}
                                                    <button
                                                        type="button"
                                                        className="btn cus-seconday-bg-btn"
                                                        onClick={() => setCsvModalShow(true)}
                                                        disabled={!isEditable}
                                                        title="Bulk Package Upload"
                                                        style={{display: 'inline-flex', alignItems: 'center'}}
                                                    >
                                                        <i className="fa fa-upload" style={{marginRight: '8px'}}></i>
                                                        <span>Bulk Package Upload</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Package Form */}
                                {showPackageForm && (
                                    <div className="card">
                                        <div className="card-header bg-light">
                                            <h6 className="mb-0" style={{display: 'flex', alignItems: 'center'}}>
                                                <i className="fa fa-box" style={{marginRight: '8px'}}></i>
                                                <span>{editingPackageIndex >= 0 ? 'Edit Package Details' : 'Add New Package'}</span>
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            {/* Dimensions and Weight Row */}
                                            <div className='row mb-3'>
                                                <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                    <label htmlFor="length">Length <span className='carrier-connect-require-field'>*</span></label>
                                                    <input
                                                        className="form-control"
                                                        type="number"
                                                        id="length"
                                                        name="length"
                                                        min="0"
                                                        value={currentPackage.length}
                                                        onChange={handlePackageFormValue}
                                                        disabled={shouldDisableComponent()}
                                                        placeholder="0"
                                                        onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                                    />
                                                    {errors[`package_${editingPackageIndex}_length`] && <div className="carrier-connect-error">{errors[`package_${editingPackageIndex}_length`]}</div>}
                                                </div>
                                            <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                <label htmlFor="width">Width <span className='carrier-connect-require-field'>*</span></label>
                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    id="width"
                                                    name="width"
                                                    min="0"
                                                    value={currentPackage.width}
                                                    onChange={handlePackageFormValue}
                                                    disabled={shouldDisableComponent()}
                                                    placeholder="0"
                                                    onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                                />
                                                {errors[`package_${editingPackageIndex}_width`] && <div className="carrier-connect-error">{errors[`package_${editingPackageIndex}_width`]}</div>}
                                            </div>
                                            <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                <label htmlFor="height">Height <span className='carrier-connect-require-field'>*</span></label>
                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    id="height"
                                                    name="height"
                                                    min="0"
                                                    value={currentPackage.height}
                                                    onChange={handlePackageFormValue}
                                                    disabled={shouldDisableComponent()}
                                                    placeholder="0"
                                                    onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                                />
                                                {errors[`package_${editingPackageIndex}_height`] && <div className="carrier-connect-error">{errors[`package_${editingPackageIndex}_height`]}</div>}
                                            </div>
                                            <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                <label htmlFor="dimensionUnitId">Dimension Unit <span className='carrier-connect-require-field'>*</span></label>
                                                <Select
                                                    closeMenuOnSelect={true}
                                                    options={dimensionUnitList?.map((dimension) => ({
                                                        value: dimension.id,
                                                        label: dimension.name
                                                    }))}
                                                    classNamePrefix="list"
                                                    id='packageDimensionUnitId'
                                                    name="dimensionUnitId"
                                                    value={dimensionUnitList?.find(option => option.id === currentPackage.dimensionUnitId) ? {
                                                        value: dimensionUnitList.find(option => option.id === currentPackage.dimensionUnitId).id,
                                                        label: dimensionUnitList.find(option => option.id === currentPackage.dimensionUnitId).name
                                                    } : null}
                                                    onChange={(selectedOption) => {
                                                        const selectedValue = selectedOption ? selectedOption.value : '';
                                                        setCurrentPackage(prev => {
                                                            let next = { ...prev, dimensionUnitId: selectedValue };
                                                            try {
                                                                // If InchEnum -> set weight unit to LB, otherwise set to KG
                                                                if (Number(selectedValue) === InchEnum) {
                                                                    next.weightUnitId = WEIGHT_UNIT_LB;
                                                                } else if (selectedValue) {
                                                                    next.weightUnitId = WEIGHT_UNIT_KG;
                                                                }
                                                            } catch (e) {
                                                                // ignore mapping errors
                                                            }
                                                            return next;
                                                        });
                                                        setErrors(prev => ({ ...prev, [`package_${editingPackageIndex}_dimensionUnitId`]: '' }));
                                                    }}
                                                    placeholder="Select Unit"
                                                    isDisabled={shouldDisableComponent()}
                                                    styles={{
                                                        control: (provided) => ({
                                                            ...provided,
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            boxShadow: 'none',
                                                            minHeight: '28px',
                                                            fontSize: '12px',
                                                            '&:hover': {
                                                                borderColor: '#007bff'
                                                            }
                                                        }),
                                                        menu: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                            position: 'absolute',
                                                            width: '100%',
                                                            minWidth: '100%',
                                                            maxWidth: '100%'
                                                        }),
                                                        menuList: (provided) => ({
                                                            ...provided,
                                                            padding: '2px 0',
                                                            maxHeight: '200px'
                                                        }),
                                                        option: (provided, state) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            cursor: 'pointer',
                                                            backgroundColor: selectOptionBackground(state),
                                                            color: state.isSelected ? 'white' : '#495057',
                                                            '&:hover': {
                                                                backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                            }
                                                        }),
                                                        placeholder: (provided) => ({
                                                            ...provided,
                                                            whiteSpace: 'nowrap',
                                                            color: '#5f6264ff',
                                                            fontSize: '12.5px'
                                                        }),
                                                        noOptionsMessage: (provided) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            color: '#6c757d'
                                                        }),
                                                        menuPortal: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                    menuPortalTarget={document.body}
                                                />
                                                {errors[`package_${editingPackageIndex}_dimensionUnitId`] && <div className="carrier-connect-error">{errors[`package_${editingPackageIndex}_dimensionUnitId`]}</div>}
                                            </div>
                                            <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                <label htmlFor="weight">Weight <span className='carrier-connect-require-field'>*</span></label>
                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    id="weight"
                                                    name="weight"
                                                    min="0"
                                                    value={currentPackage.weight}
                                                    onChange={handlePackageFormValue}
                                                    disabled={shouldDisableComponent()}
                                                    placeholder="0"
                                                    onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                                />
                                                {errors[`package_${editingPackageIndex}_weight`] && <div className="carrier-connect-error">{errors[`package_${editingPackageIndex}_weight`]}</div>}
                                            </div>
                                            <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                <label htmlFor="weightUnitId">Weight Unit <span className='carrier-connect-require-field'>*</span></label>
                                                <Select
                                                    closeMenuOnSelect={true}
                                                    options={weightUnitList?.map((weight) => ({
                                                        value: weight.id,
                                                        label: weight.name
                                                    }))}
                                                    classNamePrefix="list"
                                                    id='packageWeightUnitId'
                                                    name="weightUnitId"
                                                    value={weightUnitList?.find(option => option.id === currentPackage.weightUnitId) ? {
                                                        value: weightUnitList.find(option => option.id === currentPackage.weightUnitId).id,
                                                        label: weightUnitList.find(option => option.id === currentPackage.weightUnitId).name
                                                    } : null}
                                                    onChange={(selectedOption) => {
                                                        const selectedValue = selectedOption ? selectedOption.value : '';
                                                        setCurrentPackage(prev => {
                                                            let next = { ...prev, weightUnitId: selectedValue };
                                                            try {
                                                                // If KG or GM -> set dimension to CMSEnum, otherwise set to InchEnum
                                                                if (Number(selectedValue) === WEIGHT_UNIT_KG || Number(selectedValue) === WEIGHT_UNIT_GM) {
                                                                    next.dimensionUnitId = CMSEnum;
                                                                } else if (selectedValue) {
                                                                    next.dimensionUnitId = InchEnum;
                                                                }
                                                            } catch (e) {
                                                                // ignore mapping errors
                                                            }
                                                            return next;
                                                        });
                                                        setErrors(prev => ({ ...prev, [`package_${editingPackageIndex}_weightUnitId`]: '' }));
                                                    }}
                                                    placeholder="Select Unit"
                                                    isDisabled={shouldDisableComponent()}
                                                    styles={{
                                                        control: (provided) => ({
                                                            ...provided,
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            boxShadow: 'none',
                                                            minHeight: '28px',
                                                            fontSize: '12px',
                                                            '&:hover': {
                                                                borderColor: '#007bff'
                                                            }
                                                        }),
                                                        menu: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                            position: 'absolute',
                                                            width: '100%',
                                                            minWidth: '100%',
                                                            maxWidth: '100%'
                                                        }),
                                                        menuList: (provided) => ({
                                                            ...provided,
                                                            padding: '2px 0',
                                                            maxHeight: '200px'
                                                        }),
                                                        option: (provided, state) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            cursor: 'pointer',
                                                            backgroundColor: selectOptionBackground(state),
                                                            color: state.isSelected ? 'white' : '#495057',
                                                            '&:hover': {
                                                                backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                            }
                                                        }),
                                                        placeholder: (provided) => ({
                                                            ...provided,
                                                            whiteSpace: 'nowrap',
                                                            color: '#5f6264ff',
                                                            fontSize: '12.5px'
                                                        }),
                                                        noOptionsMessage: (provided) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            color: '#6c757d'
                                                        }),
                                                        menuPortal: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                    menuPortalTarget={document.body}
                                                />
                                                {errors[`package_${editingPackageIndex}_weightUnitId`] && <div className="carrier-connect-error">{errors[`package_${editingPackageIndex}_weightUnitId`]}</div>}
                                            </div>
                                        </div>
                                        
                                        {/* Tracking Number, Carrier, Service, Package Row */}
                                        <div className='row mb-3'>
                                            <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                <label htmlFor="trackingNumber">Tracking Number</label>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    id="trackingNumber"
                                                    name="trackingNumber"
                                                    value={currentPackage.trackingNumber}
                                                    onChange={handlePackageFormValue}
                                                    disabled={shouldDisableComponent()}
                                                    placeholder="Enter unique tracking number for this package"
                                                />
                                                {errors[`package_${editingPackageIndex}_trackingNumber`] && <div className="carrier-connect-error">{errors[`package_${editingPackageIndex}_trackingNumber`]}</div>}
                                            </div>
                                            <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                <label htmlFor="packageType">Package Type <span className='carrier-connect-require-field'>*</span></label>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    id="packageType"
                                                    name="packageType"
                                                    value={currentPackage.packageType}
                                                    onChange={handlePackageFormValue}
                                                    disabled={shouldDisableComponent()}
                                                    placeholder="Enter package type"
                                                />
                                            </div>
                                            <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                <label htmlFor="carrierCompanyId">Carrier <span className='carrier-connect-require-field'>*</span></label>
                                                <Select
                                                    closeMenuOnSelect={true}
                                                    options={services?.map((item, index) => ({
                                                        value: item.carrierCompanyId,
                                                        label: item.carrierCompanyName,
                                                        key: `carrier-${item.carrierCompanyId || index}`
                                                    }))}
                                                    classNamePrefix="list"
                                                    id='packageCarrierId'
                                                    name="carrierCompanyId"
                                                    value={services?.find(option => option.carrierCompanyId === currentPackage.carrierCompanyId) ? {
                                                        value: services.find(option => option.carrierCompanyId === currentPackage.carrierCompanyId).carrierCompanyId,
                                                        label: services.find(option => option.carrierCompanyId === currentPackage.carrierCompanyId).carrierCompanyName
                                                    } : null}
                                                    onChange={(selectedOption) => {
                                                        setCurrentPackage(prev => ({
                                                            ...prev,
                                                            carrierCompanyId: selectedOption ? selectedOption.value : '',
                                                            serviceId: '' // Clear service when carrier changes
                                                        }));
                                                        setErrors(prev => ({ ...prev, [`package_${editingPackageIndex}_carrierCompanyId`]: '' }));
                                                    }}
                                                    placeholder="Select Carrier"
                                                    isDisabled={shouldDisableComponent()}
                                                    styles={{
                                                        control: (provided) => ({
                                                            ...provided,
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            boxShadow: 'none',
                                                            minHeight: '28px',
                                                            fontSize: '12px',
                                                            '&:hover': {
                                                                borderColor: '#007bff'
                                                            }
                                                        }),
                                                        menu: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                            position: 'absolute',
                                                            width: '100%',
                                                            minWidth: '100%',
                                                            maxWidth: '100%'
                                                        }),
                                                        menuList: (provided) => ({
                                                            ...provided,
                                                            padding: '2px 0',
                                                            maxHeight: '200px'
                                                        }),
                                                        option: (provided, state) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            cursor: 'pointer',
                                                            backgroundColor: selectOptionBackground(state),
                                                            color: state.isSelected ? 'white' : '#495057',
                                                            '&:hover': {
                                                                backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                            }
                                                        }),
                                                        placeholder: (provided) => ({
                                                            ...provided,
                                                            whiteSpace: 'nowrap',
                                                            color: '#5f6264ff',
                                                            fontSize: '12.5px'
                                                        }),
                                                        noOptionsMessage: (provided) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            color: '#6c757d'
                                                        }),
                                                        menuPortal: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                    menuPortalTarget={document.body}
                                                />
                                                {errors[`package_${editingPackageIndex}_carrierCompanyId`] && <div className="carrier-connect-error">{errors[`package_${editingPackageIndex}_carrierCompanyId`]}</div>}
                                            </div>
                                            <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                <label htmlFor="serviceId">Service <span className='carrier-connect-require-field'>*</span></label>
                                                <Select
                                                    closeMenuOnSelect={true}
                                                    options={servicePackages?.services?.map((item) => ({
                                                        value: item.id,
                                                        label: item.serviceName
                                                    }))}
                                                    classNamePrefix="list"
                                                    id='packageServiceId'
                                                    name="serviceId"
                                                    value={servicePackages?.services?.find(option => option.id === currentPackage.serviceId) ? {
                                                        value: servicePackages.services.find(option => option.id === currentPackage.serviceId).id,
                                                        label: servicePackages.services.find(option => option.id === currentPackage.serviceId).serviceName
                                                    } : null}
                                                    onChange={(selectedOption) => {
                                                        setCurrentPackage(prev => ({
                                                            ...prev,
                                                            serviceId: selectedOption ? selectedOption.value : '',
                                                            packageId: '' // Clear package when service changes
                                                        }));
                                                        setErrors(prev => ({ ...prev, [`package_${editingPackageIndex}_serviceId`]: '' }));
                                                    }}
                                                    placeholder="Select Service"
                                                    isDisabled={shouldDisableComponent()}
                                                    styles={{
                                                        control: (provided) => ({
                                                            ...provided,
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            boxShadow: 'none',
                                                            minHeight: '28px',
                                                            fontSize: '12px',
                                                            '&:hover': {
                                                                borderColor: '#007bff'
                                                            }
                                                        }),
                                                        menu: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                            position: 'absolute',
                                                            width: '100%',
                                                            minWidth: '100%',
                                                            maxWidth: '100%'
                                                        }),
                                                        menuList: (provided) => ({
                                                            ...provided,
                                                            padding: '2px 0',
                                                            maxHeight: '200px'
                                                        }),
                                                        option: (provided, state) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            cursor: 'pointer',
                                                            backgroundColor: selectOptionBackground(state),
                                                            color: state.isSelected ? 'white' : '#495057',
                                                            '&:hover': {
                                                                backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                            }
                                                        }),
                                                        placeholder: (provided) => ({
                                                            ...provided,
                                                            whiteSpace: 'nowrap',
                                                            color: '#5f6264ff',
                                                            fontSize: '12.5px'
                                                        }),
                                                        noOptionsMessage: (provided) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            color: '#6c757d'
                                                        }),
                                                        menuPortal: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                    menuPortalTarget={document.body}
                                                />
                                                {errors[`package_${editingPackageIndex}_serviceId`] && <div className="carrier-connect-error">{errors[`package_${editingPackageIndex}_serviceId`]}</div>}
                                            </div>
                                            <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                <label htmlFor="packageId">Package <span className='carrier-connect-require-field'>*</span></label>
                                                <Select
                                                    closeMenuOnSelect={true}
                                                    options={packages?.map((item) => ({
                                                        value: item.id,
                                                        label: item.packageName
                                                    }))}
                                                    classNamePrefix="list"
                                                    id='packageId'
                                                    name="packageId"
                                                    value={packages?.find(option => option.id === currentPackage.packageId) ? {
                                                        value: packages.find(option => option.id === currentPackage.packageId).id,
                                                        label: packages.find(option => option.id === currentPackage.packageId).packageName
                                                    } : null}
                                                    onChange={(selectedOption) => {
                                                        setCurrentPackage(prev => ({
                                                            ...prev,
                                                            packageId: selectedOption ? selectedOption.value : ''
                                                        }));
                                                        setErrors(prev => ({ ...prev, [`package_${editingPackageIndex}_packageId`]: '' }));
                                                    }}
                                                    placeholder="Select Package"
                                                    isDisabled={shouldDisableComponent()}
                                                    styles={{
                                                        control: (provided) => ({
                                                            ...provided,
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da',
                                                            boxShadow: 'none',
                                                            minHeight: '28px',
                                                            fontSize: '12px',
                                                            '&:hover': {
                                                                borderColor: '#007bff'
                                                            }
                                                        }),
                                                        menu: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                            position: 'absolute',
                                                            width: '100%',
                                                            minWidth: '100%',
                                                            maxWidth: '100%'
                                                        }),
                                                        menuList: (provided) => ({
                                                            ...provided,
                                                            padding: '2px 0',
                                                            maxHeight: '200px'
                                                        }),
                                                        option: (provided, state) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            cursor: 'pointer',
                                                            backgroundColor: selectOptionBackground(state),
                                                            color: state.isSelected ? 'white' : '#495057',
                                                            '&:hover': {
                                                                backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                            }
                                                        }),
                                                        placeholder: (provided) => ({
                                                            ...provided,
                                                            whiteSpace: 'nowrap',
                                                            color: '#5f6264ff',
                                                            fontSize: '12.5px'
                                                        }),
                                                        noOptionsMessage: (provided) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '12px',
                                                            color: '#6c757d'
                                                        }),
                                                        menuPortal: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                    menuPortalTarget={document.body}
                                                />
                                                {errors[`package_${editingPackageIndex}_packageId`] && <div className="carrier-connect-error">{errors[`package_${editingPackageIndex}_packageId`]}</div>}
                                            </div>
                                            <div className='col-lg-2 col-md-4 col-sm-6 carrier-connect-mobMb-10'>
                                                <label htmlFor="packageCount">Package Count <i className="fa fa-info-circle text-info tooltipWidth" 
                                                    id="package-details-count-tooltip-icon"></i></label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    id="packageCount"
                                                    name="packageCount"
                                                    value={currentPackage.packageCount}
                                                    onChange={handlePackageFormValue}
                                                    disabled={shouldDisableComponent()}
                                                    placeholder="Enter package count"
                                                    min="0"
                                                />
                                                {errors[`package_${editingPackageIndex}_packageCount`] && <div className="carrier-connect-error">{errors[`package_${editingPackageIndex}_packageCount`]}</div>}
                                            </div>
                                        </div>

                                        {/* Images and Attachments Row for Package */}
                                        <div className='row mb-3'>
                                            <div className='col-lg-3 col-md-6 col-sm-12 carrier-connect-mobMb-10'>
                                                <label htmlFor="packageImageUpload">Images</label>
                                                <div className='uploadContainer'>
                                                    <div className="inlineBlock fileUpload inventory">
                                                        <button
                                                            disabled={shouldDisableComponent()}
                                                            type="button"
                                                            className="btn cus-seconday-bg-btn imgUploadbtn"
                                                            onClick={() => document.getElementById('packageImageUpload').click()}
                                                        >
                                                            Upload Images
                                                        </button>
                                                        <input
                                                            id="packageImageUpload"
                                                            name="packageImageUpload"
                                                            className="hiddenFileField"
                                                            onChange={handlePackageImageUpload}
                                                            disabled={shouldDisableComponent()}
                                                            type="file"
                                                            multiple
                                                            accept="image/*"
                                                            style={{ display: 'none' }}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    {packageImages.length > 0 && (
                                                        <div className="row">
                                                            {packageImages.map((item, index) => {
                                                                // Check if this is an existing image with URL or a new uploaded file
                                                                const imageUrl = item.imageUrl || item.filePath || item.url || (item.file && URL.createObjectURL(item.file));
                                                                const fileName = item.fileName || item.name || `Package Image ${index + 1}`;
                                                                
                                                                if (imageUrl) {
                                                                    // Show thumbnail for images
                                                                    return (
                                                                        <div key={item?.id || index} className="col-auto mb-2">
                                                                            <div className="position-relative">
                                                                                {renderImageThumbnail({ imageUrl, fileName }, index, true)}
                                                                                {isEditable && !isInTransitStatus() && (
                                                                                    <button
                                                                                        className="btn btn-danger btn-sm position-absolute"
                                                                                        style={{ top: '5px', right: '5px', padding: '2px 6px', fontSize: '10px' }}
                                                                                        onClick={() => handleDeletePackageImage(index)}
                                                                                        type="button"
                                                                                        title="Delete Image"
                                                                                    >
                                                                                        <i className="fa fa-times" aria-hidden="true"></i>
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                } else {
                                                                    // Fallback to filename display for non-image files or missing URLs
                                                                    return (
                                                                        <div className="selectedFile mr-2" key={item?.id || index}>
                                                                            <div className="fileName">{fileName}</div>
                                                                            {isEditable && !isInTransitStatus() && (
                                                                                <button
                                                                                    className="deleteImg"
                                                                                    onClick={() => handleDeletePackageImage(index)}
                                                                                    type="button"
                                                                                >
                                                                                    <i className="fa fa-times" aria-hidden="true"></i>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-lg-4 col-md-6 col-sm-12 carrier-connect-mobMb-10">
                                                <label htmlFor="packageAttachmentUpload">Attachments</label>
                                                <div className='uploadContainer'>
                                                    <div className="inlineBlock fileUpload inventory">
                                                        <button
                                                            disabled={shouldDisableComponent()}
                                                            type="button"
                                                            className="btn cus-seconday-bg-btn"
                                                            onClick={() => document.getElementById('packageAttachmentUpload').click()}
                                                        >
                                                            Upload Attachments
                                                        </button>
                                                        <input
                                                            id="packageAttachmentUpload"
                                                            name="packageAttachmentUpload"
                                                            className="hiddenFileField"
                                                            onChange={handlePackageAttachmentUpload}
                                                            disabled={shouldDisableComponent()}
                                                            type="file"
                                                            multiple
                                                            style={{ display: 'none' }}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    {packageAttachments.length > 0 &&
                                                        packageAttachments.map((item, index) => (
                                                            <>
                                                                {!item.isDeleted && (
                                                                    <div className="selectedFile mr-2" key={item?.id}>
                                                                        <div className="fileName">
                                                                            {item.fileUrl ? (
                                                                                <a
                                                                                    href={item.fileUrl}
                                                                                    target="_blank"
                                                                                    rel="noreferrer"
                                                                                >
                                                                                    {item.name}
                                                                                </a>
                                                                            ) : (
                                                                                <>{item.name}</>
                                                                            )}
                                                                        </div>
                                                                        {isEditable && !isInTransitStatus() && (
                                                                            <button
                                                                                className="deleteFile"
                                                                                onClick={() => handleDeletePackageAttachment(index)}
                                                                                type="button"
                                                                            >
                                                                                <i className="fa fa-times" aria-hidden="true"></i>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </>
                                                        ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Package Notes */}
                                        <div className='row mb-3'>
                                            <div className='col-12'>
                                                <label htmlFor="notes">Notes</label>
                                                <textarea
                                                    className="form-control"
                                                    id="notes"
                                                    name="notes"
                                                    value={currentPackage.notes}
                                                    onChange={handlePackageFormValue}
                                                    disabled={shouldDisableComponent()}
                                                    rows="3"
                                                    placeholder="Add any additional notes about this package..."
                                                />
                                            </div>
                                        </div>

                                        {/* Created Date - shown for existing packages */}
                                        {editingPackageIndex >= 0 && currentPackage.createdDate && (
                                            <div className='row mb-3'>
                                                <div className='col-lg-3 col-md-6 col-sm-12'>
                                                    <label htmlFor="createdDate">Created Date</label>
                                                    <input
                                                        className="form-control"
                                                        type="text"
                                                        id="createdDate"
                                                        value={currentPackage.createdDate ? new Date(currentPackage.createdDate).toLocaleString() : 'N/A'}
                                                        disabled={true}
                                                        readOnly
                                                        style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                            {/* Package Form Action Buttons */}
                                            <div className='row mb-3'>
                                                <div className='col-12'>
                                                    <button
                                                        type="button"
                                                        className="btn cus-seconday-bg-btn me-2"
                                                        onClick={handleSavePackage}
                                                        disabled={shouldDisableComponent()}
                                                        style={{display: 'inline-flex', alignItems: 'center'}}
                                                    >
                                                        <i className="fa fa-save" style={{marginRight: '8px'}}></i>
                                                        <span>{editingPackageIndex >= 0 ? 'Update Package' : 'Save Package'}</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn cus-primary-transparent-btn"
                                                        onClick={handleCancelPackageForm}
                                                        disabled={shouldDisableComponent()}
                                                        style={{display: 'inline-flex', alignItems: 'center'}}
                                                    >
                                                        <i className="fa fa-times" style={{marginRight: '8px'}}></i>
                                                        <span>Cancel</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className='row mb-3 mt-3'>
                                    <div className='col-12'>
                                        <label htmlFor="notes">Notes</label>
                                        <textarea
                                            className="form-control"
                                            id="notes"
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleFormValue}
                                            disabled={shouldDisableMainNotes()}
                                            rows="7"
                                            placeholder="Add any additional notes about this carrier connection..."
                                        />
                                    </div>
                                </div>

                                {/* Additional notes field for Warehouse Manager, Warehouse Worker, and Super Admin */}
                                {/* Only visible in edit mode and when status is NOT New and NOT Closed */}
                                {canSeeAdditionalNotes() && Number(id) > 0 && isEditable && !isNewStatus() && !isClosedStatus() && (
                                    <div className='row mb-3'>
                                        <div className='col-12'>
                                            <label htmlFor="additionalNotes">Additional Notes</label>
                                            <textarea
                                                className="form-control"
                                                id="additionalNotes"
                                                name="additionalNotes"
                                                value={additionalNotes}
                                                onChange={(e) => setAdditionalNotes(e.target.value)}
                                                disabled={shouldDisableComponent()}
                                                rows="2"
                                                placeholder="Add additional notes that will be appended to the main notes field..."
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Viewing Modal */}
            {showImageModal && (
                <ViewImage 
                    selectedRecord={selectedImageRecord}
                    showHideImageModal={showImageModal}
                    setShowHideImageModal={setShowImageModal}
                />
            )}

            {/* Package View Modal */}
            {showPackageViewModal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header modalDeleteWrapper">
                                <h5 className="modal-title">
                                    <i className="fa fa-box me-2"></i>
                                    Package Details - {selectedPackageForView.trackingNumber || 'No Tracking Number'}
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={handleClosePackageViewModal}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="container-fluid">
                                    {/* Package Information */}
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <h6 className="text-primary border-bottom pb-2 mb-3">
                                                <i className="fa fa-info-circle me-2 pr-1"></i>Package Information
                                            </h6>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-muted" htmlFor="trackingNumber">Tracking Number:</label>
                                            <p className="fw-bold">{selectedPackageForView.trackingNumber || 'Not provided'}</p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-muted" htmlFor="packageType">Package Type:</label>
                                            <p className="fw-bold">
                                                {selectedPackageForView.packageType || 
                                                 selectedPackageForView.pkgType || 'Not specified'}
                                            </p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-muted" htmlFor="dimensions">Dimensions:</label>
                                            <p className="fw-bold">
                                                {selectedPackageForView.length && selectedPackageForView.width && selectedPackageForView.height ? (
                                                    <>
                                                        {selectedPackageForView.length} × {selectedPackageForView.width} × {selectedPackageForView.height}{' '}
                                                        {dimensionUnitList.find(d => d.id == selectedPackageForView.dimensionUnitId)?.name || ''}
                                                    </>
                                                ) : 'Not provided'}
                                            </p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-muted" htmlFor="weight">Weight:</label>
                                            <p className="fw-bold">
                                                {selectedPackageForView.weight ? (
                                                    <>
                                                        {selectedPackageForView.weight}{' '}
                                                        {weightUnitList.find(w => w.id == selectedPackageForView.weightUnitId)?.name || ''}
                                                    </>
                                                ) : 'Not provided'}
                                            </p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-muted" htmlFor="packageCount">Package Count:</label>
                                            <p className="fw-bold">{selectedPackageForView.packageCount || 'Not provided'}</p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-muted" htmlFor="carrier">Carrier:</label>
                                            <p className="fw-bold">
                                                {services?.find(s => s.carrierCompanyId == selectedPackageForView.carrierCompanyId)?.carrierCompanyName || 
                                                 selectedPackageForView.carrierName || 'Not specified'}
                                            </p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-muted" htmlFor="service">Service:</label>
                                            <p className="fw-bold">
                                                {(() => {
                                                    // services is a list of carrier entries; each carrier has a .services array
                                                    const carrier = services?.find(s => s.carrierCompanyId == selectedPackageForView.carrierCompanyId);
                                                    let svc = null;
                                                    if (carrier?.services) {
                                                        svc = carrier.services.find(service => service.id == selectedPackageForView.serviceId);
                                                    }
                                                    return svc?.serviceName || svc?.name || selectedPackageForView.serviceName || 'Not specified';
                                                })()}
                                            </p>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-muted" htmlFor="package">Package:</label>
                                            <p className="fw-bold">
                                                {packages?.find(p => p.id == selectedPackageForView.packageId)?.packageName || 
                                                 selectedPackageForView.packageTypeName || 
                                                 selectedPackageForView.pkgType || 'Not specified'}
                                            </p>
                                        </div>
                                        {selectedPackageForView.createdDate && (
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label text-muted" htmlFor="createdDate">Created Date:</label>
                                                <p className="fw-bold">{new Date(selectedPackageForView.createdDate).toLocaleString()}</p>
                                            </div>
                                        )}
                                        {selectedPackageForView.notes && (
                                            <div className="col-12 mb-3">
                                                <label className="form-label text-muted" htmlFor="notes">Notes:</label>
                                                <p className="fw-bold">{selectedPackageForView.notes}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Package Images */}
                                    {selectedPackageForView.images && selectedPackageForView.images.length > 0 && (
                                        <div className="row mb-4">
                                            <div className="col-12">
                                                <h6 className="text-primary border-bottom pb-2 mb-3">
                                                    <i className="fa fa-images me-2"></i>Package Images ({selectedPackageForView.images.length})
                                                </h6>
                                                <div className="d-flex flex-wrap gap-3">
                                                    {selectedPackageForView.images.map((imageItem, index) => 
                                                        renderImageThumbnail(imageItem, index, true)
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Package Attachments */}
                                    {selectedPackageForView.attachments && selectedPackageForView.attachments.length > 0 && (
                                        <div className="row mb-4">
                                            <div className="col-12">
                                                <h6 className="text-primary border-bottom pb-2 mb-3">
                                                    <i className="fa fa-paperclip me-2"></i>Package Attachments ({selectedPackageForView.attachments.length})
                                                </h6>
                                                <div>
                                                    {selectedPackageForView.attachments.map((attachmentItem, index) => 
                                                        renderAttachmentItem(attachmentItem, index)
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* No Media Message */}
                                    {(!selectedPackageForView.images || selectedPackageForView.images.length === 0) && 
                                     (!selectedPackageForView.attachments || selectedPackageForView.attachments.length === 0) && (
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="alert alert-info text-center">
                                                    <i className="fa fa-info-circle me-2 pr-1"></i> No images or attachments available for this package.
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={handleClosePackageViewModal}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Charge Carrier Connect Modal */}
            {csvModalShow && (
                <BulkPackageUploadDialog
                    onHide={() => setCsvModalShow(false)}
                    show={csvModalShow}
                    type={"carrier connect"}
                    renderData={(rows) => handleAddPackagesFromCsv(rows)}
                    validateAndMapRows={(rows) => validateAndMapRows(rows)}
                />
            )}

            {showCharge && (
                <ChargeCarrierConnect 
                    show={showCharge} 
                    carrierConnectId={id} 
                    carrierConnectData={carrierConnectDetails}
                    userDetails={userDetails}
                    handleCancel={() => setShowCharge(false)}
                    fetchData={fetchCarrierConnectById}
                />
            )}

            {/* React Tooltips */}
            <Tooltip anchorId="package-count-tooltip-icon" place="right" className="tooltipWidth">
                <div>How many packages are you shipping in total?<br/>Enter the complete number of packages for this entire shipment.</div>
            </Tooltip>
            <Tooltip anchorId="package-details-count-tooltip-icon" place="right" className="tooltipWidth">
                <div>How many packages of this type are you adding?<br/>Enter the quantity for this specific package entry.</div>
            </Tooltip>
            <Tooltip anchorId="chargesBtn" place="right" className="tooltipWidth fontFix">
                <div>Show Charges History</div>
            </Tooltip>
            {showChargesDetails && (
                <ChargesHistory
                    show={showChargesDetails} 
                    handleCancel={() => setShowChargesDetails(false)} 
                    data={formData}
                />
            )}

            <AlertDialogModal
                modalShow={showChargeAlert}
                handleCancelDeleteAlert={() => setShowChargeAlert(false)}
                modalClass={"modalDeleteWrapper"}
                messageText="This Carrier Connect has already been charged. Do you want to charge it again?"
                handleYesDeleteAlert={handleOverrideCharge}
                headingText={"POST CHARGES"}
            />
        </main>
    );
};

CarrierConnectConsole.propTypes = {
    employeePermissions: PropTypes.array
};

export default CarrierConnectConsole;
